import React, { useEffect, useState, useCallback, useMemo, useRef } from 'react';
import { MapContainer, TileLayer, CircleMarker, Marker, Popup, useMap, useMapEvents, LayersControl, LayerGroup, GeoJSON } from 'react-leaflet';
import MarkerClusterGroup from 'react-leaflet-cluster';
import { useNavigate } from 'react-router-dom';
import L from 'leaflet';
import { RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis, Radar, ResponsiveContainer } from 'recharts';
import { getParcelPoints, getMineralPoints, getParcelBoundaries, getFederalLandBoundaries, getMiningClaimBoundaries, getZoningBoundaries, getParcel } from '../services/api';
import EsriFeatureLayer from './EsriFeatureLayer';
import VectorTileLayer from './VectorTileLayer';
import {
  gridCommodityForLayer,
  commodityHeatmapTileUrl,
  isRetiredCommodityLayer,
} from '../utils/commodityHeatmap';
import { safeStopMap } from '../utils/leafletTeardown';
import { isApnLikeQuery } from '../utils/apnQuery';

// #936 — HOLD commodity ML predictions from users until the label redesign
// lands. The de-leaked v3.8 model has weak/anti-predictive spatial-CV skill in
// the top mining states (gold AUC: NV 0.30, WA 0.28, OR 0.39) driven by a
// degenerate label (49-83% positive rate from POSITIVE_RADIUS_KM=5km), so the
// "predicted probability" + "combined" heatmap layers must not be presented as
// confident geology. Factual "known mines" (USGS MRDS) and hazard layers stay
// live. Re-expose by setting REACT_APP_COMMODITY_PREDICTIONS=true once the
// redesigned model clears a per-state AUC bar (see #936 acceptance).
const COMMODITY_PREDICTIONS_ENABLED =
  process.env.REACT_APP_COMMODITY_PREDICTIONS === 'true';
import HeatmapLayer from './HeatmapLayer';
import HazardTractsDebugLayer from './HazardTractsDebugLayer';
import InsarLayer, { InsarLegend, INSAR_OVERLAY_NAME } from './InsarLayer';
import GenerationLayer, { GenerationLegend, GENERATION_OVERLAY_NAME } from './GenerationLayer';
import TransmissionLayer, { TransmissionLegend, TRANSMISSION_OVERLAY_NAME } from './TransmissionLayer';
import { fmtDom } from '../utils/format';
import { ownerColor, ownerStroke } from '../utils/ownerColor';
import { parcelPath } from '../utils/parcelPath';

/* ---------- Commodity probability display ---------- */
// #1021: the popup radar must agree with the card, detail ring, and filter.
// Those surfaces render the SERVER-computed commodity percentile (0-99) that
// the backend attaches to each parcel point under `percentiles[*_prob]`, and
// fall back to the raw `prob*100` only when that field is absent (older cached
// responses). This mirrors the #1027 pattern in ParcelDetail/PropertyCard
// exactly (no client-side rescale), so all four surfaces read from one axis.
//
// The prior hardcoded COMMODITY_NORM floor/ceil map + client min-max rescale
// was a THIRD independent scale that disagreed with everything else; it is
// removed here.
//
// Exported for unit testing. `point` is a parcel point (from /parcels/points);
// `key` is a `*_prob` field name (e.g. 'gold_prob').
export function popupCommodityValue(point, key) {
  if (!point) return 0;
  const pct = point.percentiles ? point.percentiles[key] : undefined;
  if (pct != null) return Math.round(pct);
  const raw = point[key];
  return raw != null ? Math.round(raw * 100) : 0;
}

/* ---------- Mineral commodity color map ---------- */
const COMMODITY_COLORS = {
  Gold:       { fill: '#FFD700', border: '#B8860B' },
  Silver:     { fill: '#C0C0C0', border: '#808080' },
  Copper:     { fill: '#B87333', border: '#8B4513' },
  Iron:       { fill: '#A52A2A', border: '#8B0000' },
  Uranium:    { fill: '#39FF14', border: '#228B22' },
  Lead:       { fill: '#4A4A4A', border: '#2F2F2F' },
  Zinc:       { fill: '#7EC8E3', border: '#4682B4' },
  Tungsten:   { fill: '#708090', border: '#2F4F4F' },
  Manganese:  { fill: '#9370DB', border: '#6A0DAD' },
  Chromium:   { fill: '#00CED1', border: '#008B8B' },
  Lithium:    { fill: '#FF69B4', border: '#C71585' },
  Platinum:   { fill: '#E5E4E2', border: '#A9A9A9' },
  Nickel:     { fill: '#AAAA55', border: '#666633' },
  Cobalt:     { fill: '#0047AB', border: '#002E6B' },
  Geothermal: { fill: '#FF4500', border: '#CC3700' },
};
const DEFAULT_MINE_COLOR = { fill: '#ff9900', border: '#ff6600' };

// Exported for unit testing. Also called by in-file mine marker
// rendering to pick the diamond fill/border color per commodity.
export function getMineColor(commodity) {
  if (!commodity) return DEFAULT_MINE_COLOR;
  // Check primary commodity (first word before comma)
  const primary = commodity.split(',')[0].trim();
  return COMMODITY_COLORS[primary] || DEFAULT_MINE_COLOR;
}

// Diamond icon factory for mineral deposit markers (distinct from parcel circles)
const _diamondIconCache = {};
function getDiamondIcon(fillColor, borderColor) {
  const key = `${fillColor}-${borderColor}`;
  if (_diamondIconCache[key]) return _diamondIconCache[key];
  const svg = `<svg width="14" height="14" viewBox="0 0 14 14" xmlns="http://www.w3.org/2000/svg">
    <rect x="2" y="2" width="10" height="10" rx="1" transform="rotate(45 7 7)"
      fill="${fillColor}" stroke="${borderColor}" stroke-width="1.5"/>
  </svg>`;
  const icon = L.divIcon({
    html: svg,
    className: '',
    iconSize: [14, 14],
    iconAnchor: [7, 7],
    popupAnchor: [0, -8],
  });
  _diamondIconCache[key] = icon;
  return icon;
}

// Commodity filter options (top mineral types)
const COMMODITY_FILTER_OPTIONS = [
  'All', 'Gold', 'Silver', 'Copper', 'Iron', 'Uranium',
  'Lead', 'Zinc', 'Tungsten', 'Manganese', 'Chromium',
  'Lithium', 'Geothermal',
];

// Development status filter options
const STATUS_FILTER_OPTIONS = [
  { value: 'All', label: 'All Types' },
  { value: 'Producer', label: 'Active Mines' },
  { value: 'Past Producer', label: 'Past Mines' },
  { value: 'Prospect', label: 'Prospects' },
  { value: 'Occurrence', label: 'Occurrences' },
  { value: 'Plant', label: 'Plants' },
];

// Status badge colors
const STATUS_COLORS = {
  Producer:        { bg: '#27ae60', text: '#fff' },
  'Past Producer': { bg: '#e67e22', text: '#fff' },
  Prospect:        { bg: '#3498db', text: '#fff' },
  Occurrence:      { bg: '#9b59b6', text: '#fff' },
  Plant:           { bg: '#95a5a6', text: '#fff' },
  Unknown:         { bg: '#bdc3c7', text: '#333' },
};

// Debounce helper. Exported for unit testing — it's a pure React
// hook with no Leaflet dependencies, so it's easy to cover directly.
//
// The cleanup effect clears any pending timer on unmount so we don't
// fire setState on an unmounted component (React 18 warns; React 19
// will throw). #81/H5.
export function useDebouncedCallback(callback, delay) {
  const timer = useRef(null);
  useEffect(() => () => {
    if (timer.current) {
      clearTimeout(timer.current);
      timer.current = null;
    }
  }, []);
  return useCallback((...args) => {
    if (timer.current) clearTimeout(timer.current);
    timer.current = setTimeout(() => callback(...args), delay);
  }, [callback, delay]);
}

// ---------------------------------------------------------------------------
// #1131 — session-scoped persistence of map UI settings.
//
// Which base layer is showing and which overlay toggles are on (minerals,
// parcel boundaries, federal lands, mining claims, zoning, heatmap, 3D) are
// remembered for the browser session so navigating away from the map and back
// restores the user's chosen layers. sessionStorage (not localStorage) keeps
// this session-scoped rather than a permanent preference — it mirrors the
// existing SearchPage loadSavedState/saveState convention (STORAGE_KEY
// 'landai_map_state'); a distinct key avoids colliding with it.
// ---------------------------------------------------------------------------
export const MAP_LAYER_STORAGE_KEY = 'landai_map_layers';

// Read the persisted layer settings object. Returns {} on missing data or any
// parse/access error (private-mode SecurityError, corrupt JSON) so callers can
// fall back to defaults. Exported for unit testing.
export function loadLayerSettings() {
  try {
    const raw = sessionStorage.getItem(MAP_LAYER_STORAGE_KEY);
    if (raw) return JSON.parse(raw) || {};
  } catch { /* ignore */ }
  return {};
}

// Merge a patch into the persisted layer settings. Silently swallows
// quota/security errors so a full or blocked sessionStorage never crashes the
// map. Exported for unit testing.
export function saveLayerSettings(patch) {
  try {
    const next = { ...loadLayerSettings(), ...patch };
    sessionStorage.setItem(MAP_LAYER_STORAGE_KEY, JSON.stringify(next));
  } catch { /* ignore */ }
}

// useState variant that persists its value to sessionStorage under
// MAP_LAYER_STORAGE_KEY[key]. Reads the persisted value (falling back to
// `defaultValue`) on first mount, then writes on every change — so the toggle
// survives a MapView unmount/remount within the session (#1131). Exported for
// unit testing.
export function usePersistedSetting(key, defaultValue) {
  const [value, setValue] = useState(() => {
    const saved = loadLayerSettings();
    return Object.prototype.hasOwnProperty.call(saved, key) ? saved[key] : defaultValue;
  });
  useEffect(() => {
    saveLayerSettings({ [key]: value });
  }, [key, value]);
  return [value, setValue];
}

// Valid base-layer names for the topright LayersControl. A persisted value
// outside this set (corrupt storage) falls back to the Satellite default so
// exactly one base layer is always checked.
const BASE_LAYER_NAMES = ['Satellite', 'Topo', 'Terrain', 'Street'];
const DEFAULT_BASE_LAYER = 'Satellite';

const MAX_PARCEL_POINTS_BBOX_AREA_DEG2 = 100;

// #1132 — minimum zoom at which the lower-left "Parcel Boundaries" REST
// overlay (source b: the for-sale/active-listing outlines the owner uses to
// judge property sizes) loads and renders. Lowered from 12 to 8 so the yellow
// for-sale outlines stay visible when zoomed much further out. 8 is the floor
// at which land parcels are still large enough to read as shapes rather than
// sub-pixel dots; the payload stays bounded by the endpoint's 500-feature cap
// and, for the for-sale filter the owner runs, is a small set of polygons.
const BOUNDARY_MIN_ZOOM = 8;

function getBBoxAreaDeg2(bounds) {
  if (!bounds) return 0;
  const west = Number(bounds.getWest());
  const south = Number(bounds.getSouth());
  const east = Number(bounds.getEast());
  const north = Number(bounds.getNorth());
  if (![west, south, east, north].every(Number.isFinite)) return Infinity;
  return Math.abs(east - west) * Math.abs(north - south);
}

// Component that listens for map movements and loads data
// Geocode a query string via Nominatim and fly the map to the result.
// Returns early (no-op) when q is empty, results are ambiguous, or the
// result is already inside the current viewport.  US-only (countrycodes=us).
async function geocodeAndFly(map, q) {
  if (!q || q.trim().length < 3) return;
  try {
    const url = `https://nominatim.openstreetmap.org/search?q=${encodeURIComponent(q.trim())}&format=json&limit=1&countrycodes=us`;
    const res = await fetch(url, { headers: { 'Accept-Language': 'en' } });
    if (!res.ok) return;
    const results = await res.json();
    if (!results.length) return;
    const { boundingbox } = results[0];
    // Nominatim returns [south, north, west, east]
    const south = parseFloat(boundingbox[0]);
    const north = parseFloat(boundingbox[1]);
    const west  = parseFloat(boundingbox[2]);
    const east  = parseFloat(boundingbox[3]);
    if (isNaN(south) || isNaN(north) || isNaN(west) || isNaN(east)) return;
    // Fly when the result is meaningfully different from the current
    // view — either bigger (zoom out / different region) OR smaller
    // (refinement, e.g. "Texas" → "Houston, Texas"). #193 reported
    // that the prior "skip if center is in view" rule killed the
    // refinement case: Houston's center IS inside the Texas-wide
    // bounds, so the map never zoomed in.
    //
    // Area-ratio test: square-degree extent of the geocoded result vs.
    // the current map bounds. Skip only when the two areas are within
    // a 0.5-2.0 ratio (roughly the same view); fly otherwise.
    const cb = map.getBounds();
    const curArea = Math.max(1e-9, (cb.getNorth() - cb.getSouth()) * (cb.getEast() - cb.getWest()));
    const newArea = Math.max(1e-9, (north - south) * (east - west));
    const ratio = newArea / curArea;
    if (ratio > 0.5 && ratio < 2.0) {
      // Roughly the same view — also check the center is in-frame so
      // we don't skip when the user has panned away to an unrelated
      // area of similar zoom.
      const midLat = (south + north) / 2;
      const midLon = (west + east) / 2;
      if (cb.contains([midLat, midLon])) return;
    }
    map.flyToBounds([[south, west], [north, east]], { duration: 1.2, maxZoom: 14 });
  } catch {
    // Nominatim failure is non-fatal; map just stays where it is.
  }
}

function ViewportLoader({ filters, onDataLoaded, onLoadingChange, onBoundsChange }) {
  const map = useMap();
  const abortRef = useRef(null);
  const geocodeTimerRef = useRef(null);
  const lastGeocodedQ = useRef(null);

  const loadData = useCallback(async () => {
    const bounds = map.getBounds();
    const bbox = [
      bounds.getWest(),
      bounds.getSouth(),
      bounds.getEast(),
      bounds.getNorth(),
    ].join(',');

    onBoundsChange(bbox);

    // Backend rejects /parcels/points bboxes above 100 deg². ParcelDetail
    // route transitions can briefly leave the shared search map mounted at
    // the default continental-US view before the detail hero zooms in; skip
    // that transient wide request instead of logging an avoidable 400. (#810)
    if (getBBoxAreaDeg2(bounds) > MAX_PARCEL_POINTS_BBOX_AREA_DEG2) {
      if (abortRef.current) {
        abortRef.current.abort();
        abortRef.current = null;
      }
      onDataLoaded({ count: 0, limit: 5000, points: [], bbox_too_large: true });
      onLoadingChange(false);
      return;
    }

    // Cancel any in-flight request. Real AbortController so the
    // underlying axios call actually unsubscribes — the previous
    // boolean flag let stale responses race past us and clobber state.
    if (abortRef.current) abortRef.current.abort();
    const controller = new AbortController();
    abortRef.current = controller;

    onLoadingChange(true);
    try {
      // Build filter params (exclude empty values)
      const params = {};
      if (filters.state) params.state = filters.state;
      if (filters.minArea) params.min_acres = filters.minArea;
      if (filters.maxArea) params.max_acres = filters.maxArea;
      if (filters.q) params.q = filters.q;
      if (filters.isListed) params.is_listed = true;
      // Off-market only — explicit `false` so backend filters out
      // listed parcels rather than treating it as "no filter". (#99)
      if (filters.offMarketOnly) params.is_listed = false;
      if (filters.maxMineDist) params.max_mine_dist = filters.maxMineDist;
      if (filters.minMinesNearby) params.min_mines_nearby = filters.minMinesNearby;
      // #997 alt-energy generation-site proximity. Param names match the
      // backend `/parcels` contract; `generation_tech` only applies with
      // a distance cap set.
      if (filters.maxGenerationDist) {
        params.max_dist_generation_km = filters.maxGenerationDist;
        if (filters.generationTech) params.generation_tech = filters.generationTech;
      }
      // #991 HIFLD transmission-line proximity. Backend `max_powerline_dist`
      // is METRES (compared against feature_vectors.distance_to_power_line
      // and used as the voltage/class/type match radius), so convert the
      // km-valued frontend filter → metres. The voltage/class/type filters
      // require a matching line nearby (default 8 km radius when no distance
      // is set), so they stand alone without a distance cap.
      if (filters.maxPowerlineDist) {
        params.max_powerline_dist = filters.maxPowerlineDist * 1000;
      }
      if (filters.minPowerlineVoltage) params.min_powerline_voltage = filters.minPowerlineVoltage;
      if (filters.powerlineVoltClass) params.powerline_volt_class = filters.powerlineVoltClass;
      if (filters.powerlineLineType) params.powerline_line_type = filters.powerlineLineType;
      if (filters.minPrice) params.min_price = filters.minPrice;
      if (filters.maxPrice) params.max_price = filters.maxPrice;
      if (filters.minPricePerAcre) params.min_price_per_acre = filters.minPricePerAcre;
      if (filters.maxPricePerAcre) params.max_price_per_acre = filters.maxPricePerAcre;
      if (filters.priceChangedRecently) {
        params.price_changed_within_days = true;
        if (filters.priceChangeDays) {
          params.price_change_days = filters.priceChangeDays;
        }
      }
      if (filters.minGoldProb) params.min_gold_prob = filters.minGoldProb;
      if (filters.minCopperProb) params.min_copper_prob = filters.minCopperProb;
      if (filters.minSilverProb) params.min_silver_prob = filters.minSilverProb;
      if (filters.minUraniumProb) params.min_uranium_prob = filters.minUraniumProb;
      if (filters.minLithiumProb) params.min_lithium_prob = filters.minLithiumProb;
      if (filters.minReeProb) params.min_ree_prob = filters.minReeProb;
      // Niobium (#990) — parity with the six commodities above.
      if (filters.minNiobiumProb) params.min_niobium_prob = filters.minNiobiumProb;
      if (filters.domFilter) {
        if (filters.domFilter.startsWith('<')) {
          params.max_dom = filters.domFilter.slice(1);
        } else if (filters.domFilter.startsWith('>')) {
          params.min_dom = filters.domFilter.slice(1);
        }
      }
      // The "Motivated seller" quick-screen sets `filters.minDom` directly
      // (not via the domFilter dropdown). Honour it here too so the map and
      // list agree — otherwise the quick-screen filtered only the list (#1063).
      if (filters.minDom != null && filters.minDom !== '' && params.min_dom == null) {
        const n = parseInt(filters.minDom, 10);
        if (Number.isFinite(n)) params.min_dom = n;
      }
      if (filters.maxDom != null && filters.maxDom !== '' && params.max_dom == null) {
        const n = parseInt(filters.maxDom, 10);
        if (Number.isFinite(n)) params.max_dom = n;
      }
      if (filters.maxWildfireRisk) params.max_wildfire_risk = filters.maxWildfireRisk;
      if (filters.maxFloodRisk) params.max_flood_risk = filters.maxFloodRisk;
      if (filters.minElevationChange) params.min_elevation_change = filters.minElevationChange;
      if (filters.maxElevationChange) params.max_elevation_change = filters.maxElevationChange;
      // Encumbrance exclusions (#610) — map/list parity. Backend
      // applies has_lien=TRUE / has_easement=TRUE exclusion; NULL
      // passes so we don't hide parcels lacking encumbrance data.
      if (filters.excludeLiens) params.exclude_liens = true;
      if (filters.excludeEasements) params.exclude_easements = true;
      // Quick-screen filters (#629/#630) — keep map and list in lock
      // step when the FilterPanel preset is on.
      if (filters.distressedOnly) params.distressed_only = true;
      if (filters.landOnlyConfirmed) params.land_only_confirmed = true;
      // Landlocked exclusion (#641 C1 Phase 5) — map/list parity.
      if (filters.excludeLandlocked) params.exclude_landlocked = true;
      // Description-NER booleans (#781 / INIT-description-ner). Each is
      // a tri-state filter: only the TRUE branch is wired to the API.
      // The backend EXISTS-joins listing_media so the map and list
      // resolve the same set when these fire.
      if (filters.gatedCommunityOnly) params.gated_community_only = true;
      if (filters.noHoaOnly) params.no_hoa_only = true;
      if (filters.subdividableOnly) params.subdividable_only = true;
      // Photo-content classifier booleans (#783 / INIT-photo-content).
      // Aggregated per-listing booleans driven by Claude-vision per-photo
      // tags; backend EXISTS-joins listing_media. Map + list parity so
      // toggling these doesn't desynchronize the dot count.
      if (filters.hasHousePhotoOnly) params.has_house_photo_only = true;
      if (filters.hasWaterPhotoOnly) params.has_water_photo_only = true;
      if (filters.hasLongViewPhotoOnly) params.has_long_view_photo_only = true;
      // Investment-thesis tag filter (#771 / INIT-thesis-tagger) —
      // single-tag filter at confidence >=40, applied via EXISTS on
      // parcel_thesis_tags in the backend. Same key used by SearchPage
      // URL state and the deep link from ParcelDetail chips.
      if (filters.thesisTag) params.thesis_tag = filters.thesisTag;
      // Climate-risk consolidator (#773 / INIT-climate-risk) — caps
      // the consolidated 0-100 score (lower = safer). Backend
      // excludes parcels without an NRI tract match when this is set.
      if (filters.maxClimateRisk != null && filters.maxClimateRisk !== '') {
        params.max_climate_risk = filters.maxClimateRisk;
      }
      // Listing-quality predictor floor (#775 / INIT-listing-quality)
      // — minimum per-LISTING composite score. Backend excludes
      // parcels without an active scored listing when this is set.
      if (filters.minListingQuality != null && filters.minListingQuality !== '') {
        params.min_listing_quality = filters.minListingQuality;
      }
      // Sale-probability predictor floor (#777 / INIT-sale-probability).
      // The slider lives in FilterPanel as an integer percent (0-100);
      // SearchPage downconverts to a 0-1 float before passing through
      // `minSaleProbability60d`. Backend excludes parcels without a
      // scored active listing when this is set.
      if (filters.minSaleProbability60d != null && filters.minSaleProbability60d !== '') {
        // FilterPanel stores 0-100 (UI grain); backend expects 0-1.
        // Defensive divide-by-100 if a stray percent slips through; if
        // already a float the conditional below leaves it alone.
        const v = Number(filters.minSaleProbability60d);
        params.min_sale_probability_60d = v > 1 ? v / 100 : v;
      }
      // Fair-value max-percent-above filter (#779 / INIT-fair-value).
      // The slider lives in FilterPanel as a percent in [-50, 500];
      // backend takes the same raw percent (no 0-1 downconversion
      // needed). Excludes listings without a populated fair_value_*
      // triple when this is set.
      if (filters.maxPctAboveFairValue != null && filters.maxPctAboveFairValue !== '') {
        params.max_pct_above_fair_value = Number(filters.maxPctAboveFairValue);
      }
      // Investment-yield floor (#785 / INIT-yield-calc). Filter to active
      // listings whose expected_5y_yield_pct is at or above the chosen
      // value. Same shape as min_listing_quality (a percent, no 0-1
      // downconversion); backend partial index drives the EXISTS lookup.
      if (filters.minExpectedYield != null && filters.minExpectedYield !== '') {
        params.min_expected_yield = Number(filters.minExpectedYield);
      }

      const data = await getParcelPoints(bbox, params, 5000, { signal: controller.signal });
      if (!controller.signal.aborted) {
        onDataLoaded(data);
      }
    } catch (err) {
      if (!controller.signal.aborted) {
        console.error('Failed to load parcel points:', err);
        onDataLoaded({ count: 0, limit: 5000, points: [] });
      }
    } finally {
      // Clear loading only if we're still the latest in-flight
      // controller. The original code returned early on abort,
      // which could leave loading stuck-true when the *only*
      // loadData call was aborted before completing (e.g. fast
      // viewport changes followed by a network drop). Now: if a
      // newer call has set abortRef to a different controller,
      // that newer call owns the loading flag — don't clobber it.
      // If we ARE still abortRef.current (no successor scheduled),
      // we must clear loading regardless of abort status.  (#81 H2)
      if (abortRef.current === controller) {
        onLoadingChange(false);
      }
    }
  }, [map, filters, onDataLoaded, onLoadingChange, onBoundsChange]);

  const debouncedLoad = useDebouncedCallback(loadData, 400);

  // Load on map move/zoom
  useMapEvents({
    moveend: debouncedLoad,
    zoomend: debouncedLoad,
  });

  // Load on initial render and when filters change
  useEffect(() => {
    // Small delay to let the map initialize
    const t = setTimeout(loadData, 200);
    return () => {
      clearTimeout(t);
      if (abortRef.current) abortRef.current.abort();
    };
  }, [loadData]);

  // Geocode the text query and fly the map there when q changes.
  // Debounced 600ms so we don't fire on every keystroke. Skipped
  // when q is the same as the last geocoded value (prevents
  // re-flying after the map moves and re-renders ViewportLoader).
  useEffect(() => {
    const q = filters.q;
    if (geocodeTimerRef.current) clearTimeout(geocodeTimerRef.current);
    if (!q || q === lastGeocodedQ.current) return;
    // #1176: APN/parcel-number-looking queries can never resolve on
    // Nominatim (or worse, resolve to an unrelated numeric road/house
    // match and fly the camera somewhere bogus). SearchPage resolves
    // these against the parcel q-search and drives FocusFlyer to the
    // parcel boundary instead — skip the place geocode entirely.
    if (isApnLikeQuery(q)) return;
    geocodeTimerRef.current = setTimeout(() => {
      lastGeocodedQ.current = q;
      geocodeAndFly(map, q);
    }, 600);
    return () => { if (geocodeTimerRef.current) clearTimeout(geocodeTimerRef.current); };
  }, [filters.q, map]);

  return null;
}

// Component that loads mine markers when the overlay is enabled
function MineOverlayLoader({ enabled, commodityFilter, statusFilter, onMinesLoaded }) {
  const map = useMap();
  const abortRef = useRef(null);

  const loadMines = useCallback(async () => {
    if (!enabled) {
      onMinesLoaded([]);
      return;
    }
    const bounds = map.getBounds();
    const bbox = [
      bounds.getWest(), bounds.getSouth(),
      bounds.getEast(), bounds.getNorth(),
    ].join(',');

    if (abortRef.current) abortRef.current.abort();
    const controller = new AbortController();
    abortRef.current = controller;

    try {
      const commodityParam = (commodityFilter && commodityFilter !== 'All') ? commodityFilter : null;
      const statusParam = (statusFilter && statusFilter !== 'All') ? statusFilter : null;
      const data = await getMineralPoints(bbox, commodityParam, 5000, statusParam);
      if (!controller.signal.aborted) {
        onMinesLoaded(data.points || []);
      }
    } catch (err) {
      if (controller.signal.aborted) return;
      console.error('Failed to load mineral points:', err);
      onMinesLoaded([]);
    }
  }, [map, enabled, commodityFilter, statusFilter, onMinesLoaded]);

  const debouncedLoad = useDebouncedCallback(loadMines, 500);

  useMapEvents({
    moveend: debouncedLoad,
    zoomend: debouncedLoad,
  });

  useEffect(() => {
    const t = setTimeout(loadMines, 300);
    return () => {
      clearTimeout(t);
      if (abortRef.current) abortRef.current.abort();
    };
  }, [loadMines]);

  return null;
}

// Component that loads parcel boundary polygons when zoomed in enough.
// `enabled` gates both the fetch and the rendered overlay — without it
// the loader fired unconditionally at zoom >= 12 and the parent
// rendered the resulting <GeoJSON> regardless of any user toggle, so
// parcel boundaries appeared on the map without being explicitly
// enabled (issue #110). The "Parcel Boundaries (Vector Tiles)" entry
// in LayersControl toggles a SEPARATE vector-tile layer; this REST
// boundary overlay needs its own gate.
function BoundaryLoader({ enabled, filters, onBoundariesLoaded }) {
  const map = useMap();
  const abortRef = useRef(null);

  const loadBoundaries = useCallback(async () => {
    if (!enabled) {
      onBoundariesLoaded(null);
      return;
    }
    const zoom = map.getZoom();
    // #1132 — load the for-sale/active-listing outlines at zoom >=
    // BOUNDARY_MIN_ZOOM (lowered from 12 to 8) so the yellow for-sale outlines
    // stay visible when zoomed much further out. This is the lower-left
    // "Parcel Boundaries" REST source (b), NOT the topright vector-tile
    // source (a); only source (b) is extended, per #1132.
    if (zoom < BOUNDARY_MIN_ZOOM) {
      onBoundariesLoaded(null);
      return;
    }
    const bounds = map.getBounds();
    const bbox = [
      bounds.getWest(), bounds.getSouth(),
      bounds.getEast(), bounds.getNorth(),
    ].join(',');

    if (abortRef.current) abortRef.current.abort();
    const controller = new AbortController();
    abortRef.current = controller;

    try {
      const params = {};
      if (filters.isListed) params.is_listed = true;
      // Off-market only — explicit `false` so backend filters out
      // listed parcels rather than treating it as "no filter". (#99)
      if (filters.offMarketOnly) params.is_listed = false;
      const data = await getParcelBoundaries(bbox, params, 500, { signal: controller.signal });
      if (!controller.signal.aborted) {
        onBoundariesLoaded(data);
      }
    } catch (err) {
      if (controller.signal.aborted) return;
      console.error('Failed to load boundaries:', err);
      onBoundariesLoaded(null);
    }
  }, [map, enabled, filters, onBoundariesLoaded]);

  const debouncedLoad = useDebouncedCallback(loadBoundaries, 500);

  useMapEvents({
    moveend: debouncedLoad,
    zoomend: debouncedLoad,
  });

  useEffect(() => {
    const t = setTimeout(loadBoundaries, 300);
    return () => {
      clearTimeout(t);
      if (abortRef.current) abortRef.current.abort();
    };
  }, [loadBoundaries]);

  return null;
}

/* ---------- Federal land agency colors ---------- */
const AGENCY_COLORS = {
  BLM:  '#F4A460',   // sandy brown
  USFS: '#228B22',   // forest green
  FS:   '#228B22',
  NPS:  '#6B8E23',   // olive
  FWS:  '#4682B4',   // steel blue
  BOR:  '#4169E1',   // royal blue
  USBR: '#4169E1',
  BIA:  '#CD853F',   // peru
  DOD:  '#808080',   // gray
  DOE:  '#DAA520',   // goldenrod
};

const AGENCY_LABELS = {
  BLM:  'Bureau of Land Management',
  USFS: 'US Forest Service',
  FS:   'US Forest Service',
  NPS:  'National Park Service',
  FWS:  'US Fish & Wildlife Service',
  BOR:  'Bureau of Reclamation',
  USBR: 'Bureau of Reclamation',
  BIA:  'Bureau of Indian Affairs',
  DOD:  'Dept. of Defense',
  DOE:  'Dept. of Energy',
};

// Component that loads federal land boundaries when the overlay is enabled
function FederalLandsLoader({ enabled, onFederalLandsLoaded }) {
  const map = useMap();
  const abortRef = useRef(null);

  const loadFederalLands = useCallback(async () => {
    if (!enabled) {
      onFederalLandsLoaded(null);
      return;
    }
    const bounds = map.getBounds();
    const bbox = [
      bounds.getWest(), bounds.getSouth(),
      bounds.getEast(), bounds.getNorth(),
    ].join(',');

    if (abortRef.current) abortRef.current.abort = true;
    const thisReq = { abort: false };
    abortRef.current = thisReq;

    try {
      const data = await getFederalLandBoundaries(bbox, map.getZoom());
      if (!thisReq.abort) {
        onFederalLandsLoaded(data);
      }
    } catch (err) {
      console.error('Failed to load federal lands:', err);
      if (!thisReq.abort) onFederalLandsLoaded(null);
    }
  }, [map, enabled, onFederalLandsLoaded]);

  const debouncedLoad = useDebouncedCallback(loadFederalLands, 500);

  useMapEvents({
    moveend: debouncedLoad,
    zoomend: debouncedLoad,
  });

  useEffect(() => {
    const t = setTimeout(loadFederalLands, 300);
    return () => {
      clearTimeout(t);
      // Abort any in-flight request when the component unmounts so
      // its then-handler doesn't fire setState on an unmounted parent
      // (causes React "memory leak" warnings in dev mode).
      if (abortRef.current) abortRef.current.abort = true;
    };
  }, [loadFederalLands]);

  return null;
}

/* ---------- Mining claim type colors ---------- */
const CLAIM_TYPE_COLORS = {
  'LODE CLAIM':   '#E74C3C',
  'PLACER CLAIM': '#F39C12',
  'MILL SITE':    '#9B59B6',
  'TUNNEL SITE':  '#3498DB',
};

// Component that loads mining claim boundaries when the overlay is enabled
function MiningClaimsLoader({ enabled, onMiningClaimsLoaded }) {
  const map = useMap();
  const abortRef = useRef(null);

  const loadMiningClaims = useCallback(async () => {
    if (!enabled) {
      onMiningClaimsLoaded(null);
      return;
    }
    const bounds = map.getBounds();
    const bbox = [
      bounds.getWest(), bounds.getSouth(),
      bounds.getEast(), bounds.getNorth(),
    ].join(',');

    if (abortRef.current) abortRef.current.abort = true;
    const thisReq = { abort: false };
    abortRef.current = thisReq;

    try {
      const data = await getMiningClaimBoundaries(bbox);
      if (!thisReq.abort) {
        onMiningClaimsLoaded(data);
      }
    } catch (err) {
      console.error('Failed to load mining claims:', err);
      if (!thisReq.abort) onMiningClaimsLoaded(null);
    }
  }, [map, enabled, onMiningClaimsLoaded]);

  const debouncedLoad = useDebouncedCallback(loadMiningClaims, 500);

  useMapEvents({
    moveend: debouncedLoad,
    zoomend: debouncedLoad,
  });

  useEffect(() => {
    const t = setTimeout(loadMiningClaims, 300);
    return () => {
      clearTimeout(t);
      if (abortRef.current) abortRef.current.abort = true;
    };
  }, [loadMiningClaims]);

  return null;
}

/* ---------- Zoning category colors ---------- */
const ZONE_CATEGORY_COLORS = {
  'agricultural':  '#8BC34A',
  'residential':   '#2196F3',
  'commercial':    '#FF9800',
  'industrial':    '#9E9E9E',
  'public':        '#9C27B0',
  'mixed-use':     '#E91E63',
  'conservation':  '#4CAF50',
  'transitional':  '#FFC107',
  'other':         '#607D8B',
  'unknown':       '#BDBDBD',
};

// Component that loads zoning district boundaries when the overlay is enabled
function ZoningLoader({ enabled, onZoningLoaded }) {
  const map = useMap();
  const abortRef = useRef(null);

  const loadZoning = useCallback(async () => {
    if (!enabled) {
      onZoningLoaded(null);
      return;
    }
    const bounds = map.getBounds();
    const bbox = [
      bounds.getWest(), bounds.getSouth(),
      bounds.getEast(), bounds.getNorth(),
    ].join(',');

    if (abortRef.current) abortRef.current.abort = true;
    const thisReq = { abort: false };
    abortRef.current = thisReq;

    try {
      const data = await getZoningBoundaries(bbox);
      if (!thisReq.abort) {
        onZoningLoaded(data);
      }
    } catch (err) {
      console.error('Failed to load zoning districts:', err);
      if (!thisReq.abort) onZoningLoaded(null);
    }
  }, [map, enabled, onZoningLoaded]);

  const debouncedLoad = useDebouncedCallback(loadZoning, 500);

  useMapEvents({
    moveend: debouncedLoad,
    zoomend: debouncedLoad,
  });

  useEffect(() => {
    const t = setTimeout(loadZoning, 300);
    return () => {
      clearTimeout(t);
      if (abortRef.current) abortRef.current.abort = true;
    };
  }, [loadZoning]);

  return null;
}

// Exported for unit testing. Also called by in-file parcel popup
// rendering to format listing prices in the marker popups.
export function formatPrice(price) {
  if (price == null) return null;
  if (price >= 1000000) return `$${(price / 1000000).toFixed(2)}M`;
  if (price >= 1000) return `$${(price / 1000).toFixed(0)}K`;
  return `$${price.toLocaleString()}`;
}

// Fly-to helper: when `focusedParcel` changes (set by the
// MapListPanel row click in the split view, #146 slice 2, or by the
// PropertyCard "Locate" button), pan the map to the parcel and zoom
// in to a useful detail level.
//
// Issue #708: the prior implementation used flyTo(lat, lon, max(currentZoom, 14)).
// If the user was already at zoom ≥ 14 with the parcel in view (very
// common in split-view where the panel only shows points already on
// the map), flyTo with the same coords + same zoom is a no-op — the
// camera never moved, so the user got no feedback that "Locate" did
// anything. Now we fetch the parcel's actual polygon and fitBounds
// to it, which always re-frames the camera around the parcel
// regardless of current zoom/center, and zooms in tighter than the
// previous z=14 floor when the parcel is small.
//
// Geometry fetch is best-effort: if it fails or the parcel has no
// polygon, we fall back to flyTo on the centroid with a fresh
// zoom (z=16) — strictly tighter than the prior max() so the camera
// still moves perceptibly even when already zoomed in.
function FocusFlyer({ focusedParcel }) {
  const map = useMap();
  // Route changes can unmount the search map while a focus fitBounds/flyTo
  // (or a geocode flyToBounds) is still animating. Abort any in-flight
  // pan/zoom on unmount — before react-leaflet detaches the map pane — so
  // Leaflet's _onZoomTransitionEnd never reads a torn-down pane and throws
  // "Cannot read properties of undefined (reading '_leaflet_pos')"
  // (#814/#815/#1060). Mirrors the FitBounds guard on the ParcelDetail hero
  // map; keyed on `map` alone so it fires only on true teardown, not on each
  // focused-parcel change.
  useEffect(() => () => safeStopMap(map), [map]);
  useEffect(() => {
    if (!focusedParcel) return;
    let cancelled = false;

    const fitToBoundary = (geometry) => {
      if (cancelled) return false;
      try {
        const layer = L.geoJSON(geometry);
        const bounds = layer.getBounds();
        if (bounds && bounds.isValid && bounds.isValid()) {
          map.fitBounds(bounds, { padding: [40, 40], maxZoom: 17, animate: true });
          return true;
        }
      } catch {
        /* invalid geometry — fall through to centroid */
      }
      return false;
    };

    // Centroid fallback. Use z=16 (one tighter than the prior z=14
    // floor) and flyTo so a same-spot click still produces a visible
    // zoom-in animation rather than a no-op.
    //
    // #1176: accepts the fetched parcel detail (when we have one) so a
    // caller that couldn't supply lat/lon (e.g. a search result row
    // without coordinates) still gets a camera move from the detail
    // endpoint's centroid. Previously a null caller-centroid was a
    // silent no-op — the map never moved and the user got zero
    // feedback that the parcel was found.
    const flyToCentroid = (fetched) => {
      if (cancelled) return;
      const lat = focusedParcel.lat ?? fetched?.latitude ?? null;
      const lon = focusedParcel.lon ?? fetched?.longitude ?? null;
      if (lat == null || lon == null) {
        // No polygon AND no centroid from any source — nothing we can
        // fly to. Leave a trace instead of failing silently (#1176).
        console.warn(
          'FocusFlyer: no boundary or centroid available for parcel',
          focusedParcel.parcel_id,
        );
        return;
      }
      map.flyTo([lat, lon], 16, { duration: 0.6 });
    };

    // 1. If the caller passed geometry already, use it directly — no
    //    fetch needed (saves a round-trip when the boundary layer
    //    already had the polygon).
    if (focusedParcel.geometry && fitToBoundary(focusedParcel.geometry)) {
      return () => { cancelled = true; };
    }

    // 2. Otherwise, fetch the parcel detail and fitBounds to its
    //    polygon. Skip the fetch only when there's literally no
    //    parcel_id to look up (defensive).
    if (focusedParcel.parcel_id) {
      getParcel(focusedParcel.parcel_id)
        .then((data) => {
          if (cancelled) return;
          if (!data || !data.geometry || !fitToBoundary(data.geometry)) {
            flyToCentroid(data);
          }
        })
        .catch((err) => {
          if (cancelled) return;
          // Boundary fetch failed — fall back to the caller's centroid,
          // and leave a trace so the failure isn't invisible (#1176).
          console.warn(
            'FocusFlyer: boundary fetch failed for parcel',
            focusedParcel.parcel_id,
            err,
          );
          flyToCentroid();
        });
      return () => { cancelled = true; };
    }

    // 3. No parcel_id, no geometry — last resort, just fly to the
    //    centroid we were given.
    flyToCentroid();
    return () => { cancelled = true; };

    // #1176: key the effect on the focusedParcel OBJECT REFERENCE, not
    // the parcel_id. Every setter (PropertyCard "Locate", SearchPage's
    // APN auto-focus, the ?fp= URL seed) creates a fresh object per
    // trigger, so re-selecting the SAME parcel after panning away
    // re-fires the zoom — the old parcel_id key made that a silent
    // no-op. Parent re-renders pass the same state reference, so the
    // "identical re-emission doesn't re-fly" property is preserved.
    // CI's react-scripts eslint doesn't load eslint-plugin-react-hooks
    // by namespaced rule name (see useUrlState.js comment for the
    // full lesson). Unqualified disable suppresses without the
    // unresolvable rule reference.
    // eslint-disable-next-line
  }, [focusedParcel]);
  return null;
}

// Component that tracks map position and reports changes
function ViewTracker({ onViewChange }) {
  const map = useMap();
  useMapEvents({
    moveend: () => {
      const c = map.getCenter();
      onViewChange([c.lat, c.lng], map.getZoom());
    },
    zoomend: () => {
      const c = map.getCenter();
      onViewChange([c.lat, c.lng], map.getZoom());
    },
  });
  return null;
}

// Hides overlay panes during base layer switch to prevent flash of markers
function BaseLayerSwitchHandler() {
  const map = useMap();
  useEffect(() => {
    const handler = () => {
      const panes = ['overlayPane', 'markerPane', 'shadowPane'];
      panes.forEach(name => {
        const pane = map.getPane(name);
        if (pane) pane.style.opacity = '0';
      });
      // Restore after tiles start loading (short delay)
      setTimeout(() => {
        panes.forEach(name => {
          const pane = map.getPane(name);
          if (pane) pane.style.opacity = '1';
        });
      }, 300);
    };
    map.on('baselayerchange', handler);
    return () => map.off('baselayerchange', handler);
  }, [map]);
  return null;
}

// #1131 — persists the LayersControl base-layer + overlay selections to
// sessionStorage. Control.Layers fires `baselayerchange` on base-layer switch
// and `overlayadd` / `overlayremove` when an overlay checkbox is toggled, each
// with an `{ name, layer }` event. We record the current base-layer name and a
// per-overlay boolean map so a MapView remount can re-seed the control's
// `checked` props from the persisted values (see initialLayerSettings below).
function LayerControlPersistence() {
  const map = useMap();
  useEffect(() => {
    const onBaseChange = (e) => {
      if (e && e.name) saveLayerSettings({ baseLayer: e.name });
    };
    const onOverlayAdd = (e) => {
      if (!e || !e.name) return;
      saveLayerSettings({ overlays: { ...(loadLayerSettings().overlays || {}), [e.name]: true } });
    };
    const onOverlayRemove = (e) => {
      if (!e || !e.name) return;
      saveLayerSettings({ overlays: { ...(loadLayerSettings().overlays || {}), [e.name]: false } });
    };
    map.on('baselayerchange', onBaseChange);
    map.on('overlayadd', onOverlayAdd);
    map.on('overlayremove', onOverlayRemove);
    return () => {
      map.off('baselayerchange', onBaseChange);
      map.off('overlayadd', onOverlayAdd);
      map.off('overlayremove', onOverlayRemove);
    };
  }, [map]);
  return null;
}

function ZoomTracker({ onZoomChange }) {
  const map = useMap();
  useMapEvents({
    zoomend: () => onZoomChange(map.getZoom()),
  });
  useEffect(() => { onZoomChange(map.getZoom()); }, [map, onZoomChange]);
  return null;
}

// ---------------------------------------------------------------------------
// Module-level GeoJSON style + popup builders.
//
// React-Leaflet's <GeoJSON> uses prop-reference equality on `style` and
// `onEachFeature` to decide whether the layer needs a full re-render.
// Inlining `style={(f) => ({...})}` inside a parent component creates a
// new function on every render, which makes every parent state change
// trigger a full layer rebuild — visible as map flicker and drops in
// pan/zoom responsiveness.
//
// Hoisting these to module scope makes the references stable. The
// functions still close over only module-level constants
// (AGENCY_COLORS, CLAIM_TYPE_COLORS, ZONE_CATEGORY_COLORS, AGENCY_LABELS).
// ---------------------------------------------------------------------------
const _federalLandsStyle = (feature) => {
  const color = feature.properties.color || AGENCY_COLORS[feature.properties.agency_code] || '#888888';
  return { color, weight: 1.5, fillColor: color, fillOpacity: 0.25, dashArray: '' };
};

const _federalLandsOnEachFeature = (feature, layer) => {
  const p = feature.properties;
  const agencyName = p.agency_name || AGENCY_LABELS[p.agency_code] || p.agency_code;
  layer.bindPopup(
    `<div style="font-size:0.85rem">` +
    `<div style="font-weight:700;margin-bottom:4px">${agencyName}</div>` +
    (p.unit_name ? `<div><strong>Unit:</strong> ${p.unit_name}</div>` : '') +
    (p.state ? `<div><strong>State:</strong> ${p.state}</div>` : '') +
    `<div style="margin-top:4px;font-size:0.75rem;color:#888">Federal mineral rights likely retained</div>` +
    `</div>`,
    { maxWidth: 260 }
  );
};

const _miningClaimsStyle = (feature) => {
  const color = feature.properties.color || CLAIM_TYPE_COLORS[feature.properties.claim_type] || '#E74C3C';
  return { color, weight: 1.5, fillColor: color, fillOpacity: 0.30, dashArray: '4 2' };
};

// #1586: the claim's `acres` field is the BLM's statutory CASE size
// (one lode case is 20.66 acres), not the area of the claim. This
// popup used to print it as "Acres:", which put "183,048,738.1" on
// a map as a land area. The polygon's MEASURED area is the number
// that belongs here; the case size stays in the API payload for
// whoever needs the legal figure.
export function miningClaimPopupHtml(p) {
  return (
    `<div style="font-size:0.85rem">` +
    `<div style="font-weight:700;margin-bottom:4px">${p.claim_name || 'Unnamed'}</div>` +
    `<div><strong>Type:</strong> ${p.claim_type || 'Unknown'}</div>` +
    `<div><strong>Serial:</strong> ${p.serial_nr || 'N/A'}</div>` +
    (p.geometry_acres != null
      ? `<div><strong>Mapped area:</strong> ${Number(p.geometry_acres).toFixed(1)} ac</div>`
      : '') +
    `<div><strong>State:</strong> ${p.state || ''}</div>` +
    (p.is_patented ? `<div style="color:#E74C3C;font-weight:600">Patented claim</div>` : '') +
    `<div style="margin-top:4px;font-size:0.75rem;color:#888">BLM Active Mining Claim (MLRS)</div>` +
    `</div>`
  );
}

const _miningClaimsOnEachFeature = (feature, layer) => {
  layer.bindPopup(miningClaimPopupHtml(feature.properties), { maxWidth: 260 });
};

const _zoningStyle = (feature) => {
  const color = feature.properties.color || ZONE_CATEGORY_COLORS[feature.properties.zone_category] || '#607D8B';
  return { color, weight: 1.5, fillColor: color, fillOpacity: 0.20, dashArray: '3 3' };
};

const _zoningOnEachFeature = (feature, layer) => {
  const p = feature.properties;
  const catLabel = (p.zone_category || 'unknown').replace(/-/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
  layer.bindPopup(
    `<div style="font-size:0.85rem">` +
    `<div style="font-weight:700;margin-bottom:4px">${p.zone_desc || p.zone_code || 'Zoning District'}</div>` +
    (p.zone_code ? `<div><strong>Code:</strong> ${p.zone_code}</div>` : '') +
    `<div><strong>Category:</strong> ${catLabel}</div>` +
    (p.county ? `<div><strong>County:</strong> ${p.county}</div>` : '') +
    (p.state ? `<div><strong>State:</strong> ${p.state}</div>` : '') +
    `</div>`,
    { maxWidth: 260 }
  );
};

const _boundariesStyle = (feature) => ({
  color: feature.properties.is_listed ? '#FFD700' : '#3498db',
  weight: feature.properties.is_listed ? 3 : 2,
  fillColor: feature.properties.is_listed ? '#FFD700' : '#3498db',
  fillOpacity: feature.properties.approximate ? 0.06 : 0.1,
  dashArray: feature.properties.approximate ? '6,4' : '',
});

// ---------------------------------------------------------------------------
// Stable identity key for GeoJSON `data` objects. WeakMap-backed counter so
// each fresh API response gets a unique numeric id; identical refs reuse
// the same id. Used as the React `key` on <GeoJSON> elements so a data
// swap forces a remount (react-leaflet's <GeoJSON> doesn't reconcile data
// internally — it only handles style updates). This replaces a parallel
// `*Key` setState counter that was forcing an extra MapView re-render on
// every overlay reload.
// ---------------------------------------------------------------------------
const _geoKeyMap = new WeakMap();
let _geoKeyCounter = 0;
function geoKey(obj) {
  if (!obj) return 0;
  let id = _geoKeyMap.get(obj);
  if (id === undefined) {
    id = ++_geoKeyCounter;
    _geoKeyMap.set(obj, id);
  }
  return id;
}

// ---------------------------------------------------------------------------
// Module-level mineral list used by the parcel popup radar chart. Previously
// declared inside renderPopup's IIFE, which re-allocated a 6-entry array
// every popup render. Hoisted to module scope so it's a stable reference.
// ---------------------------------------------------------------------------
const POPUP_MINERALS = [
  { key: 'gold_prob',    symbol: 'Au', name: 'Gold' },
  { key: 'copper_prob',  symbol: 'Cu', name: 'Copper' },
  { key: 'silver_prob',  symbol: 'Ag', name: 'Silver' },
  { key: 'uranium_prob', symbol: 'U',  name: 'Uranium' },
  { key: 'lithium_prob', symbol: 'Li', name: 'Lithium' },
  { key: 'ree_prob',     symbol: 'RE', name: 'REE' },
];

// ---------------------------------------------------------------------------
// MarkerClusterGroup cluster icon factory. Hoisted to module scope so the
// reference is stable across renders — passing a fresh inline function every
// render makes react-leaflet-cluster rebuild its cluster layer on each
// parent state change.
// ---------------------------------------------------------------------------
const createClusterIcon = (cluster) => {
  const count = cluster.getChildCount();
  let size = 'small';
  let diameter = 36;
  if (count >= 100) { size = 'large'; diameter = 50; }
  else if (count >= 10) { size = 'medium'; diameter = 42; }

  return L.divIcon({
    html: `<div class="cluster-icon cluster-${size}"><span>${count >= 1000 ? Math.round(count/1000) + 'k' : count}</span></div>`,
    className: 'custom-cluster',
    iconSize: L.point(diameter, diameter),
  });
};

// ---------------------------------------------------------------------------
// ParcelPopup — extracted from inline renderPopup() and memoized so closed
// popups stay dormant on parent re-renders. The popup body includes a recharts
// RadarChart which is the expensive part; previously that JSX was rebuilt
// for every CircleMarker on every parent state change.
//
// React.memo with the default shallow-equality check is sufficient here:
// the `point` reference is stable across renders (it comes from points state
// which only changes on data load), and `navigate` from useNavigate() is
// stable for the life of the route.
// ---------------------------------------------------------------------------
const ParcelPopup = React.memo(function ParcelPopup({ point, navigate }) {
  // Guard against undefined `point` — a CircleMarker that survives
  // a points-state replace can briefly render a popup with stale
  // refs; popping the popup body to a parcel_id-only fallback keeps
  // the user from seeing an empty black popup bar (#122).
  if (!point || !point.parcel_id) {
    return (
      <Popup maxWidth={260} minWidth={180}>
        <div className="popup-content" style={{ fontSize: '0.85rem', color: '#666' }}>
          Parcel data not loaded — try clicking again or refreshing.
        </div>
      </Popup>
    );
  }
  const addr = point.listing_address || point.listing_city;
  // Fallback display name when no address/city — use parcel_id so
  // the popup never renders a totally empty header.
  const headerTitle = addr
    || (point.county && point.state ? `${point.county}, ${point.state}` : null)
    || point.parcel_id;
  const price = formatPrice(point.listing_price);
  const acres = point.area_acres != null ? Number(point.area_acres).toLocaleString(undefined, { maximumFractionDigits: 1 }) : null;
  const pricePerAcre = (point.listing_price && point.area_acres && point.area_acres > 0)
    ? formatPrice(point.listing_price / point.area_acres)
    : null;

  const hasAnyMineral = POPUP_MINERALS.some(m => point[m.key] != null && point[m.key] > 0);
  const radarData = hasAnyMineral
    ? POPUP_MINERALS.map(m => ({
        mineral: m.symbol,
        // #1021: server percentile (0-99), falling back to raw prob*100.
        value: popupCommodityValue(point, m.key),
        raw: point[m.key] != null ? Math.round(point[m.key] * 100) : 0,
      }))
    : null;

  return (
    <Popup maxWidth={320} minWidth={220} autoPan={true} autoPanPadding={[50, 50]} keepInView={true}>
      <div className="popup-content">
        {/* Header with listing badge */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 6 }}>
          <div style={{ flex: 1 }}>
            <div style={{ fontWeight: 700, fontSize: '0.95rem', color: '#1a1a2e', marginBottom: 2 }}>{headerTitle}</div>
            {addr && (
              <div style={{ fontSize: '0.8rem', color: '#666' }}>
                {point.county && `${point.county}, `}{point.state || ''}
                {point.listing_zip && ` ${point.listing_zip}`}
              </div>
            )}
          </div>
          <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
            {point.is_listed && (
              <span style={{
                background: '#2ecc71', color: '#fff', fontSize: '0.7rem', fontWeight: 700,
                padding: '2px 8px', borderRadius: 10, whiteSpace: 'nowrap',
              }}>LISTED</span>
            )}
            {point.federal_land_agency && (
              <span style={{
                background: AGENCY_COLORS[point.federal_land_agency] || '#888',
                color: '#fff', fontSize: '0.65rem', fontWeight: 700,
                padding: '2px 6px', borderRadius: 10, whiteSpace: 'nowrap',
              }} title={`Federal land: ${AGENCY_LABELS[point.federal_land_agency] || point.federal_land_agency}`}>
                {point.federal_land_agency}
              </span>
            )}
          </div>
        </div>

        {/* Price and key metrics */}
        {price && (
          <div style={{ fontSize: '1.15rem', fontWeight: 800, color: '#e94560', marginBottom: 4 }}>
            {price}
            {pricePerAcre && <span style={{ fontSize: '0.75rem', fontWeight: 400, color: '#888', marginLeft: 6 }}>({pricePerAcre}/ac)</span>}
          </div>
        )}

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '2px 12px', fontSize: '0.82rem', color: '#444', marginBottom: 6 }}>
          {acres && <div><strong>Acres:</strong> {acres}</div>}
          {point.days_on_market != null && <div><strong>DOM:</strong> {fmtDom(point.days_on_market)}</div>}
          {point.owner && <div style={{ gridColumn: '1 / -1' }}><strong>Owner:</strong> {point.owner}</div>}
        </div>

        {/* Spider/radar chart for mineral probabilities */}
        {radarData && (
          <div style={{ width: '100%', height: 160, marginBottom: 4 }}>
            <ResponsiveContainer width="100%" height="100%">
              <RadarChart cx="50%" cy="50%" outerRadius="70%" data={radarData}>
                <PolarGrid stroke="#ddd" />
                <PolarAngleAxis dataKey="mineral" tick={{ fontSize: 11, fontWeight: 700, fill: '#555' }} />
                <PolarRadiusAxis domain={[0, 100]} tick={false} axisLine={false} />
                <Radar name="Percentile" dataKey="value" stroke="#e94560" fill="#e94560" fillOpacity={0.25} strokeWidth={2} dot={{ r: 3, fill: '#e94560' }} />
              </RadarChart>
            </ResponsiveContainer>
          </div>
        )}

        {/* Links */}
        <div style={{ display: 'flex', gap: 10, marginTop: 4 }}>
          <a href={parcelPath(point.parcel_id, { state: point.state, county: point.county })}
            onClick={(e) => {
              // Intercept only plain-click; leave cmd/ctrl/middle-click to
              // open in a new tab as usual.
              if (e.button === 0 && !e.metaKey && !e.ctrlKey && !e.shiftKey && !e.altKey) {
                e.preventDefault();
                navigate(parcelPath(point.parcel_id, { state: point.state, county: point.county }));
              }
            }}
            style={{ color: '#e94560', fontWeight: 600, fontSize: '0.85rem', textDecoration: 'none' }}>
            View Details &rarr;
          </a>
          {point.lat && point.lon && (
            <a href={`https://maps.apple.com/?ll=${point.lat},${point.lon}&q=${point.lat},${point.lon}`}
              target="_blank" rel="noopener noreferrer"
              style={{ color: '#3498db', fontSize: '0.82rem', textDecoration: 'none' }}>
              Apple Maps
            </a>
          )}
        </div>
      </div>
    </Popup>
  );
});

function MapView({ filters, selectedParcel, onSelectParcel, onMapData, initialCenter, initialZoom, onViewChange, bookmarkedIds, downvotedIds, onBoundsChange, focusedParcel, hoveredParcelId, viewedIds }) {
  const navigate = useNavigate();
  const defaultPosition = initialCenter || [39.7392, -104.9903]; // Denver, CO
  const defaultZoom = initialZoom || 5;

  // Stable click handler for parcel-boundary GeoJSON. Captures
  // `navigate` so it has to live in component scope, but useCallback
  // keeps the reference stable across renders so React-Leaflet's
  // <GeoJSON> doesn't rebuild the layer on every state change.
  const boundariesOnEachFeature = useCallback((feature, layer) => {
    layer.on('click', () => {
      if (feature.properties.parcel_id) {
        navigate(`/parcel/${encodeURIComponent(feature.properties.parcel_id)}`);
      }
    });
  }, [navigate]);

  const [points, setPoints] = useState([]);

  // Filtered point list. Without memoization the filter ran on every
  // single render — and with up to 5000 points, that turned into
  // O(n) work plus a brand-new array reference, which made the
  // MarkerClusterGroup rebuild all of its markers from scratch.
  //
  // Three layered filters:
  // 1. "My bookmarks only" — narrows to the bookmark set when on.
  // 2. Downvotes (#124) — hidden parcels drop out unless the user
  //    flipped on `filters.showHidden`, in which case they stay
  //    visible and the style memo paints them red.
  // 3. "Not viewed only" (#183) — removes already-viewed parcels.
  const visiblePoints = useMemo(() => {
    let out = points;
    if (filters.bookmarksOnly && bookmarkedIds && bookmarkedIds.size > 0) {
      out = out.filter(p => bookmarkedIds.has(p.parcel_id));
    }
    if (!filters.showHidden && downvotedIds && downvotedIds.size > 0) {
      out = out.filter(p => !downvotedIds.has(p.parcel_id));
    }
    if (filters.notViewed && viewedIds && viewedIds.size > 0) {
      out = out.filter(p => !viewedIds.has(p.parcel_id));
    }
    return out;
  }, [points, filters.bookmarksOnly, bookmarkedIds, filters.showHidden, downvotedIds, filters.notViewed, viewedIds]);

  const [dataInfo, setDataInfo] = useState({ count: 0, limit: 5000, bbox_too_large: false });
  const [loading, setLoading] = useState(false);
  // Overlay state — persisted for the session (#1131) so the user's chosen
  // overlays are restored after navigating away from the map and back.
  const [showMines, setShowMines] = usePersistedSetting('showMines', false);
  const [minePoints, setMinePoints] = useState([]);
  const [commodityFilter, setCommodityFilter] = usePersistedSetting('commodityFilter', 'All');
  const [statusFilter, setStatusFilter] = usePersistedSetting('statusFilter', 'All');
  // Boundary polygons (loaded at zoom >= BOUNDARY_MIN_ZOOM when toggle is enabled).
  //
  // `showBoundaries` defaults to false — without this gate the
  // BoundaryLoader fired on every pan/zoom and the resulting GeoJSON
  // overlay was rendered unconditionally, so users saw parcel
  // boundaries even when they hadn't asked for them (issue #110).
  // The vector-tile boundary entry in LayersControl is a separate
  // layer with its own checkbox.
  //
  // No `*Key` counter state alongside these data refs — react-leaflet's
  // <GeoJSON> only reconciles `style` changes (not `data`), so we use
  // the data identity itself as the React key (via geoKey()) to force a
  // remount on data swap. The previous setState-on-every-load was
  // forcing an extra MapView re-render with no visible benefit.
  const [showBoundaries, setShowBoundaries] = usePersistedSetting('showBoundaries', false);
  const [boundaryData, setBoundaryData] = useState(null);
  // Federal lands overlay
  const [showFederalLands, setShowFederalLands] = usePersistedSetting('showFederalLands', false);
  const [federalLandsData, setFederalLandsData] = useState(null);
  // Mining claims overlay
  const [showMiningClaims, setShowMiningClaims] = usePersistedSetting('showMiningClaims', false);
  const [miningClaimsData, setMiningClaimsData] = useState(null);
  // Zoning districts overlay
  const [showZoning, setShowZoning] = usePersistedSetting('showZoning', false);
  const [zoningData, setZoningData] = useState(null);
  // Heatmap overlay (mineral commodities + hazard layers)
  const [showHeatmap, setShowHeatmap] = usePersistedSetting('showHeatmap', false);
  // Default to a factual layer when predictions are held (#936); 'gold'
  // (combined) is a held prediction surface, so fall back to 'gold-mines'.
  const [heatmapLayer, setHeatmapLayer] = usePersistedSetting(
    'heatmapLayer',
    COMMODITY_PREDICTIONS_ENABLED ? 'gold' : 'gold-mines'
  );
  // Live-tunable L.heatLayer parameters exposed via sliders. Defaults
  // mirror HeatmapLayer's anchor-zoom values for the active layer
  // class so the initial render matches what the user already sees.
  // A useEffect below resets these when the user switches between
  // hazard and commodity layers (which have different baselines).
  const [heatRadius, setHeatRadius] = useState(28);    // commodity default
  const [heatBlur, setHeatBlur] = useState(42);        // 28 * 1.5
  const [heatMax, setHeatMax] = useState(1.0);         // commodity default
  const [heatMinOpacity, setHeatMinOpacity] = useState(0.08);
  // Global opacity for the parcel-independent commodity-grid raster
  // (the L.heatLayer radius/blur/max/minOpacity knobs don't apply to a
  // pre-rendered PNG; the tile already encodes per-pixel coverage alpha,
  // so this is a single overall blend control). See #277.
  const [heatTileOpacity, setHeatTileOpacity] = useState(0.75);
  // Debug overlay: render NRI hazard tract centroids with raw scores
  // so we can compare to what the heatmap aggregates show.
  const [showHazardDebug, setShowHazardDebug] = useState(false);

  // #1043 — "3D" perspective/tilt view of the main search map. Leaflet is a
  // 2D engine (no native pitch), so this is a CSS-perspective tilt applied to
  // the whole map surface via a `map-3d` class on the wrapper: tiles, parcel
  // markers, boundaries, heatmap and the InSAR overlay all tilt together as a
  // single plane, giving a Google-Earth-style raked terrain look without
  // pulling in a new WebGL map engine. Defaults off; pan/zoom still work
  // (interaction hit-testing is against the untilted plane, so it is a
  // presentation mode — the parcel-detail page keeps the true WebGL 3D mesh).
  const [is3D, setIs3D] = usePersistedSetting('is3D', false);

  // #1131 — read the persisted LayersControl selections once on mount so the
  // base-layer and overlay `checked` props below can be seeded from them.
  // Control.Layers reads `checked` only at construction time, so seeding on
  // remount is what restores the user's chosen base layer / vector-tile and
  // hazard overlays after navigating away and back. A persisted base-layer
  // name outside the known set falls back to the Satellite default.
  const initialLayerSettings = useRef(loadLayerSettings());
  const savedBaseLayer = BASE_LAYER_NAMES.includes(initialLayerSettings.current.baseLayer)
    ? initialLayerSettings.current.baseLayer
    : DEFAULT_BASE_LAYER;
  const savedOverlays = initialLayerSettings.current.overlays || {};

  // When the user switches the heatmap layer between a hazard and a
  // commodity (or vice-versa), reset the four sliders to that class's
  // baseline so the user doesn't carry over a tuned value that makes
  // no sense on the new layer (hazard radius=55px is way too big for
  // a commodity layer; commodity radius=28px is too small for hazard).
  const isHazardLayer =
    heatmapLayer === 'wildfire' || heatmapLayer === 'flood' ||
    heatmapLayer === 'wildfire-loss' ||
    heatmapLayer === 'wildfire-freq' || heatmapLayer === 'flood-freq';
  // When non-null, the active commodity layer renders from the
  // parcel-INDEPENDENT grid raster (/tiles/commodity-heatmap/*.png)
  // instead of the L.heatLayer per-parcel point path that leaked parcel
  // density into the color. Null → keep using L.heatLayer (-mines,
  // niobium, hazard). See #277 / utils/commodityHeatmap.js.
  const gridCommodity = gridCommodityForLayer(heatmapLayer);
  // Retired prediction layers (#1381 item 5) must render NOTHING. They are
  // no longer in GRID_COMMODITIES, so gridCommodity is null for them --
  // and null is also how a layer asks for the L.heatLayer point path,
  // which for a prediction layer is the per-parcel density artifact from
  // #277. Suppressing that path is the point of the check; an empty map
  // is honest, a density map labelled "uranium" is not. Still reachable
  // after the dropdown edit below because heatmapLayer is persisted, so a
  // selection made before the retirement survives in sessionStorage and
  // outlives a reload of the tab it was made in.
  const retiredCommodity = isRetiredCommodityLayer(heatmapLayer);
  useEffect(() => {
    if (isHazardLayer) {
      setHeatRadius(55);              // HeatmapLayer HAZARD_BASE_R
      setHeatBlur(Math.round(55 * 1.25));  // hazard blur ratio = 1.25
      setHeatMax(3.0);                // hazard default
      setHeatMinOpacity(0.08);
    } else {
      setHeatRadius(28);              // HeatmapLayer COMMODITY_BASE_R
      setHeatBlur(Math.round(28 * 1.5));   // commodity blur ratio = 1.5
      setHeatMax(1.0);                // commodity default
      setHeatMinOpacity(0.08);
    }
  }, [isHazardLayer]);
  // Current zoom level for disabling controls that require minimum zoom
  const [currentZoom, setCurrentZoom] = useState(defaultZoom);

  const handleDataLoaded = useCallback((data) => {
    const next = data.points || [];
    // Issue #699: when the user clicks a CircleMarker, Leaflet auto-
    // pans to fit the popup (autoPan=true on ParcelPopup). That pan
    // fires `moveend`, which triggers the debounced viewport reload.
    // If the new bbox still contains the same parcels (very common
    // for a tiny auto-pan), we'd previously call setPoints with a
    // brand-new array reference. That cascaded through visiblePoints
    // → parcelStyles → clusterChildren, rebuilding every CircleMarker
    // and tearing down the just-opened popup before the user could
    // see it.
    //
    // Reuse the prior `points` reference when the parcel_id+lat+lon
    // set is unchanged. parcel_id alone isn't sufficient because
    // lat/lon can drift on the centroid as backend data evolves, and
    // a marker position change should still re-render.
    setPoints((prev) => {
      if (prev.length !== next.length) return next;
      for (let i = 0; i < prev.length; i++) {
        const a = prev[i];
        const b = next[i];
        if (!a || !b) return next;
        if (a.parcel_id !== b.parcel_id) return next;
        if (a.lat !== b.lat || a.lon !== b.lon) return next;
      }
      return prev;
    });
    setDataInfo({
      count: data.count,
      limit: data.limit,
      bbox_too_large: Boolean(data.bbox_too_large),
    });
    // Don't bump a clusterKey here — react-leaflet-cluster reconciles
    // children on its own, and forcing a re-mount kills open popups
    // and cluster animations on every pan. (#81 H4)
    if (onMapData) {
      onMapData(data);
    }
  }, [onMapData]);

  const handleBoundsChange = useCallback((bbox) => {
    // Forward the bbox string ("w,s,e,n") up to any parent that wants
    // viewport awareness — primarily the SearchPage split-view panel
    // (#146 slice 1) which scopes its list to parcels currently in view.
    if (onBoundsChange) onBoundsChange(bbox);
  }, [onBoundsChange]);

  const handleMinesLoaded = useCallback((mines) => {
    setMinePoints(mines);
  }, []);

  const handleBoundariesLoaded = useCallback((data) => {
    setBoundaryData(data);
  }, []);

  const handleFederalLandsLoaded = useCallback((data) => {
    setFederalLandsData(data);
  }, []);

  const handleMiningClaimsLoaded = useCallback((data) => {
    setMiningClaimsData(data);
  }, []);

  const handleZoningLoaded = useCallback((data) => {
    setZoningData(data);
  }, []);

  const isBookmarked = useCallback(
    (point) => bookmarkedIds && bookmarkedIds.has(point.parcel_id),
    [bookmarkedIds],
  );

  // #124 — downvoted/hidden parcels. Only rendered at all when
  // `filters.showHidden` is on (see visiblePoints); when shown they
  // pick up the red style below so the user can tell them apart.
  const isDownvoted = useCallback(
    (point) => downvotedIds && downvotedIds.has(point.parcel_id),
    [downvotedIds],
  );

  // Precompute per-parcel CircleMarker pathOptions in a Map keyed by
  // parcel_id. Without memoization, every render allocated 5000 fresh
  // style objects — and each one triggered react-leaflet's CircleMarker
  // to apply a setStyle() through Leaflet's path layer, which is
  // surprisingly expensive (DOM attribute writes, SVG re-paint).
  // Now the same object reference is reused across renders unless the
  // points array or bookmarkedIds changes.
  const parcelStyles = useMemo(() => {
    const map = new Map();
    for (const point of points) {
      const bk = bookmarkedIds && bookmarkedIds.has(point.parcel_id);
      // Downvote wins over bookmark for styling — if a user has
      // both bookmarked and then hidden a parcel, the hidden state
      // is the more recent intent and is the one we surface (#124).
      const dv = downvotedIds && downvotedIds.has(point.parcel_id);
      let fillColor;
      let color;
      if (dv) {
        // Red tint for hidden parcels — only visible when
        // "Show hidden" is on (otherwise filtered out entirely).
        fillColor = '#dc2626';
        color = '#991b1b';
      } else if (bk) {
        fillColor = '#f39c12';
        color = '#e67e22';
      } else {
        fillColor = (point.is_listed && point.has_boundary)
          ? '#2ecc71'
          : point.is_listed
            ? '#95d5b2'
            : point.has_boundary
              ? '#3498db'
              : '#85c1e9';
        color = point.is_listed
          ? (point.has_boundary ? '#27ae60' : '#82c99a')
          : (point.has_boundary ? '#2980b9' : '#7fb3d8');
      }
      map.set(point.parcel_id, {
        color,
        fillColor,
        fillOpacity: (dv || bk) ? 0.95 : (point.has_boundary ? 0.85 : 0.5),
        radius: (dv || bk) ? 10 : (point.is_listed ? 8 : 6),
        weight: (dv || bk) ? 3 : (point.has_boundary ? 2 : 1),
        dashArray: (dv || bk) ? '' : (point.has_boundary ? '' : '3,3'),
      });
    }
    return map;
  }, [points, bookmarkedIds, downvotedIds]);

  // getParcelColor still used by code paths outside the marker render
  // (e.g. point list legends). Kept for API compat. Downvote-red wins
  // over bookmark-orange for the same reason as in `parcelStyles`.
  const getParcelColor = useCallback((point) => {
    if (isDownvoted(point)) return '#dc2626';
    if (isBookmarked(point)) return '#f39c12';
    if (point.is_listed && point.has_boundary) return '#2ecc71';
    if (point.is_listed) return '#95d5b2';
    if (point.has_boundary) return '#3498db';
    return '#85c1e9';
  }, [isBookmarked, isDownvoted]);

  // getParcelStyle is now a Map lookup (O(1)) instead of an object
  // allocation. Falls back to a synthesized object only if a marker
  // somehow renders for a point that isn't in the memoized list yet.
  const getParcelStyle = useCallback((point) => {
    const cached = parcelStyles.get(point.parcel_id);
    if (cached) return cached;
    const dv = isDownvoted(point);
    const bk = isBookmarked(point);
    return {
      color: dv ? '#991b1b'
        : bk ? '#e67e22'
          : point.is_listed
            ? (point.has_boundary ? '#27ae60' : '#82c99a')
            : (point.has_boundary ? '#2980b9' : '#7fb3d8'),
      fillColor: getParcelColor(point),
      fillOpacity: (dv || bk) ? 0.95 : (point.has_boundary ? 0.85 : 0.5),
      radius: (dv || bk) ? 10 : (point.is_listed ? 8 : 6),
      weight: (dv || bk) ? 3 : (point.has_boundary ? 2 : 1),
      dashArray: (dv || bk) ? '' : (point.has_boundary ? '' : '3,3'),
    };
  }, [parcelStyles, isBookmarked, isDownvoted, getParcelColor]);

  // Boundary-polygon style that adds the red tint for downvoted
  // parcels (#124). Falls back to the module-level _boundariesStyle
  // for everything else. Memoized so the <GeoJSON> doesn't see a
  // fresh function ref on every render (react-leaflet does compare
  // style refs for reconciliation).
  //
  // When `filters.colorByOwner` is on (#703 follow-up), polygons are
  // color-coded by owner via the same ownerColor() helper the
  // parcel-detail adjacent overlay uses — so cards and the map
  // visually agree on owner identity. Downvoted parcels still win.
  const boundariesStyle = useCallback((feature) => {
    if (feature?.properties?.parcel_id &&
        downvotedIds && downvotedIds.has(feature.properties.parcel_id)) {
      return {
        color: '#991b1b',
        weight: 3,
        fillColor: '#dc2626',
        fillOpacity: 0.25,
        dashArray: '',
      };
    }
    if (filters?.colorByOwner) {
      const owner = feature?.properties?.owner;
      return {
        color: ownerStroke(owner),
        weight: feature?.properties?.is_listed ? 2.5 : 1.5,
        fillColor: ownerColor(owner),
        // Slightly lower than the parcel-detail mini-map (0.45) since
        // the SearchPage may render hundreds of polygons at once.
        fillOpacity: feature?.properties?.approximate ? 0.15 : 0.30,
        dashArray: feature?.properties?.approximate ? '6,4' : '',
      };
    }
    return _boundariesStyle(feature);
  }, [downvotedIds, filters?.colorByOwner]);

  // Filter the boundary GeoJSON down the same way visiblePoints
  // filters the circle markers — drop downvoted features unless
  // the user has flipped "Show hidden" on.
  const visibleBoundaryData = useMemo(() => {
    if (!boundaryData || !boundaryData.features) return boundaryData;
    if (filters.showHidden || !downvotedIds || downvotedIds.size === 0) {
      return boundaryData;
    }
    const features = boundaryData.features.filter(
      (f) => !downvotedIds.has(f?.properties?.parcel_id),
    );
    if (features.length === boundaryData.features.length) return boundaryData;
    return { ...boundaryData, features };
  }, [boundaryData, filters.showHidden, downvotedIds]);

  // Memoize the entire MarkerClusterGroup body. With up to 5000 visible
  // points, walking and re-creating <CircleMarker> JSX on every parent
  // state change (e.g. zoom, overlay toggle) was the dominant render
  // cost. Now the children array reuses the same JSX nodes unless the
  // points list, styles, or callbacks change. Each <ParcelPopup> is also
  // React.memo'd so closed popups stay dormant. (#perf top-1)
  const clusterChildren = useMemo(
    () => visiblePoints.map((point, index) => {
      // Issue #699: the React key MUST NOT depend on `index`. When the
      // server returns the same parcels in a different order (e.g.
      // after a filter toggle or a benign reload triggered by popup
      // auto-pan), the index changes for every marker even though the
      // parcels themselves are identical — React unmounts the whole
      // marker tree and the open popup vanishes. Keying on parcel
      // identity alone keeps the marker DOM stable across reloads.
      // The `index` fallback is only used when parcel_id is missing
      // (shouldn't happen, but defensive).
      const markerKey = point.parcel_id
        || `noid:${point.lat ?? 'lat'}:${point.lon ?? 'lon'}:${index}`;
      return (
        <CircleMarker
          key={markerKey}
          center={[point.lat, point.lon]}
          pathOptions={parcelStyles.get(point.parcel_id) || getParcelStyle(point)}
          eventHandlers={{ click: () => onSelectParcel && onSelectParcel(point) }}
        >
          <ParcelPopup point={point} navigate={navigate} />
        </CircleMarker>
      );
    }),
    // getParcelStyle is intentionally omitted — it's only invoked for
    // the rare cache-miss fallback and the result is structurally
    // equivalent to the cached value, so adding it as a dep would
    // invalidate this memo on every parcelStyles update without
    // functional difference. parcelStyles already covers that case.
    [visiblePoints, parcelStyles, onSelectParcel, navigate],
  );

  return (
    <div className={`map-container${is3D ? ' map-3d' : ''}`}>
      <MapContainer
        center={defaultPosition}
        zoom={defaultZoom}
        style={{ height: '100%', width: '100%' }}
        maxZoom={18}
        minZoom={3}
      >
        <LayersControl position="topright">
          {/* ===== Base Layers ===== */}
          {/* #1131 — `checked` seeded from the session-persisted base-layer
              name so the user's choice is restored on remount. */}
          <LayersControl.BaseLayer checked={savedBaseLayer === 'Satellite'} name="Satellite">
            <TileLayer
              attribution='Tiles &copy; Esri &mdash; Source: Esri, Maxar, Earthstar Geographics'
              url="https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}"
              maxZoom={19}
            />
          </LayersControl.BaseLayer>

          <LayersControl.BaseLayer checked={savedBaseLayer === 'Topo'} name="Topo">
            <TileLayer
              attribution='Tiles &copy; Esri &mdash; Source: USGS, Esri, TANA, DeLorme'
              url="https://server.arcgisonline.com/ArcGIS/rest/services/World_Topo_Map/MapServer/tile/{z}/{y}/{x}"
              maxZoom={19}
            />
          </LayersControl.BaseLayer>

          <LayersControl.BaseLayer checked={savedBaseLayer === 'Terrain'} name="Terrain">
            <TileLayer
              attribution='Map data: &copy; <a href="https://openstreetmap.org/copyright">OpenStreetMap</a> contributors, <a href="http://viewfinderpanoramas.org">SRTM</a> | Map style: &copy; <a href="https://opentopomap.org">OpenTopoMap</a>'
              url="https://{s}.tile.opentopomap.org/{z}/{x}/{y}.png"
              maxZoom={17}
            />
          </LayersControl.BaseLayer>

          <LayersControl.BaseLayer checked={savedBaseLayer === 'Street'} name="Street">
            <TileLayer
              attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
              url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
              maxZoom={19}
            />
          </LayersControl.BaseLayer>

          {/* ===== Vector Tile Overlay Layers ===== */}
          {/* #1131 — overlay `checked` props seeded from the session-persisted
              per-overlay map so toggled overlays are restored on remount. */}
          <LayersControl.Overlay checked={!!savedOverlays['Parcel Boundaries (Vector Tiles)']} name="Parcel Boundaries (Vector Tiles)">
            <LayerGroup>
              <VectorTileLayer
                url="/tiles/parcels/{z}/{x}/{y}.pbf"
                layerName="parcels"
                onClick={(properties) => {
                  if (properties.parcel_id) {
                    navigate(`/parcel/${encodeURIComponent(properties.parcel_id)}`);
                  }
                }}
                getFeatureId={(f) => f.properties.parcel_id}
              />
            </LayerGroup>
          </LayersControl.Overlay>

          {/* ===== Hazard Overlay Layers (served via local caching proxy) ===== */}
          <LayersControl.Overlay checked={!!savedOverlays['Active Fire Perimeters']} name="Active Fire Perimeters">
            <LayerGroup>
              <EsriFeatureLayer
                url="https://services3.arcgis.com/T4QMspbfLg3qTGWY/arcgis/rest/services/WFIGS_Interagency_Perimeters_Current/FeatureServer/0"
                opacity={0.6}
                style={() => ({ color: '#FF4500', weight: 2, fillColor: '#FF6347', fillOpacity: 0.3 })}
                minZoom={4}
                attribution="NIFC/WFIGS (current year)"
                overlayName="Active Fire Perimeters"
              />
            </LayerGroup>
          </LayersControl.Overlay>

          {/* InSAR ground-motion overlay (#140). Western-US coverage
              (#1042: WA/OR/CA/NV/AZ/UT/ID/WY/CO/TX); the "(Western US)"
              suffix in the name surfaces the coverage extent directly in
              the standard layer control UI. */}
          <LayersControl.Overlay checked={!!savedOverlays[INSAR_OVERLAY_NAME]} name={INSAR_OVERLAY_NAME}>
            <LayerGroup>
              <InsarLayer />
            </LayerGroup>
          </LayersControl.Overlay>

          {/* Alt-energy generation sites (#997). Multi-tech point overlay
              (wind / solar / geothermal), sized by capacity_mw and
              clustered at low zoom. Self-managing visibility + per-tech
              toggle live inside GenerationLayer. */}
          <LayersControl.Overlay checked={!!savedOverlays[GENERATION_OVERLAY_NAME]} name={GENERATION_OVERLAY_NAME}>
            <LayerGroup>
              <GenerationLayer />
            </LayerGroup>
          </LayersControl.Overlay>

          {/* HIFLD electric power transmission lines (#991). Vector line
              overlay colored by voltage class (server-derived color/weight/
              dashed), zoom-gated to the high-voltage backbone at low zoom.
              Self-managing visibility + per-volt-class toggle live inside
              TransmissionLayer. */}
          <LayersControl.Overlay checked={!!savedOverlays[TRANSMISSION_OVERLAY_NAME]} name={TRANSMISSION_OVERLAY_NAME}>
            <LayerGroup>
              <TransmissionLayer />
            </LayerGroup>
          </LayersControl.Overlay>

          <LayersControl.Overlay checked={!!savedOverlays['USA Protected Areas (PAD-US)']} name="USA Protected Areas (PAD-US)">
            <LayerGroup>
              <VectorTileLayer
                url="/tiles/protected-areas/{z}/{x}/{y}.pbf"
                layerName="protected_areas"
                minZoom={7}
                vectorTileLayerStyles={{
                  protected_areas: (properties) => {
                    // Color by manager type
                    const colors = {
                      FED: '#F4A460', STAT: '#6B8E23', LOC: '#4682B4',
                      DIST: '#DDA0DD', JNT: '#DAA520', NGO: '#228B22',
                      PVT: '#808080', TRIB: '#CD853F',
                    };
                    const fillColor = colors[properties.manager_type] || '#2E8B57';
                    return {
                      weight: 1,
                      color: '#228B22',
                      fillColor,
                      fillOpacity: 0.2,
                      fill: true,
                    };
                  },
                }}
              />
            </LayerGroup>
          </LayersControl.Overlay>
        </LayersControl>

        <ZoomTracker onZoomChange={setCurrentZoom} />

        {/* Commodity prediction/combined heatmap — parcel-INDEPENDENT
            grid raster (#277). Renders the precomputed ~1 km
            commodity_heatmap_grid so color tracks predicted geology,
            not parcel density. Markers stay above it (tilePane < markerPane). */}
        {COMMODITY_PREDICTIONS_ENABLED && showHeatmap && gridCommodity && (
          <TileLayer
            key={`commodity-grid-${gridCommodity}`}
            url={commodityHeatmapTileUrl(gridCommodity)}
            opacity={heatTileOpacity}
            zIndex={400}
          />
        )}

        {/* Heatmap layer — L.heatLayer point path for layers that are
            genuinely NOT parcel density: known mines (real deposits),
            niobium (no grid), and hazard tracts. Disabled when a
            grid-backed commodity layer is active so the two paths never
            stack. */}
        <HeatmapLayer
          layer={heatmapLayer}
          enabled={showHeatmap && !gridCommodity && !retiredCommodity}
          radius={heatRadius}
          blur={heatBlur}
          max={heatMax}
          minOpacity={heatMinOpacity}
        />

        {/* Debug overlay: per-tract centroid + raw score so we can
            sanity-check the heatmap. Only enabled when the debug
            checkbox is on AND the active heatmap layer is wildfire/flood. */}
        {/* InSAR legend; self-managing visibility — shows only when
            the InsarLayer overlay checkbox is on. */}
        <InsarLegend />

        {/* Generation-sites legend (#997); self-managing visibility —
            collapsible, shows only when the overlay checkbox is on. */}
        <GenerationLegend />

        {/* Transmission-lines legend (#991); self-managing visibility —
            collapsible volt-class swatches + AC/DC line-style key, shows
            only when the overlay checkbox is on. */}
        <TransmissionLegend />

        <HazardTractsDebugLayer
          layer={
            (heatmapLayer === 'flood' || heatmapLayer.startsWith('flood-'))
              ? 'flood'
              : (heatmapLayer === 'wildfire-loss' ? 'wildfire-loss' : 'wildfire')
          }
          metric={heatmapLayer.endsWith('-freq') ? 'frequency' : 'risk'}
          enabled={showHazardDebug && showHeatmap && (
            heatmapLayer === 'wildfire' || heatmapLayer === 'flood' ||
            heatmapLayer === 'wildfire-loss' ||
            heatmapLayer === 'wildfire-freq' || heatmapLayer === 'flood-freq'
          )}
        />

        <ViewportLoader
          filters={filters}
          onDataLoaded={handleDataLoaded}
          onLoadingChange={setLoading}
          onBoundsChange={handleBoundsChange}
        />

        <MineOverlayLoader
          enabled={showMines}
          commodityFilter={commodityFilter}
          statusFilter={statusFilter}
          onMinesLoaded={handleMinesLoaded}
        />

        <BoundaryLoader
          enabled={showBoundaries}
          filters={filters}
          onBoundariesLoaded={handleBoundariesLoaded}
        />

        <FederalLandsLoader
          enabled={showFederalLands}
          onFederalLandsLoaded={handleFederalLandsLoaded}
        />
        <MiningClaimsLoader
          enabled={showMiningClaims}
          onMiningClaimsLoaded={handleMiningClaimsLoaded}
        />
        <ZoningLoader
          enabled={showZoning}
          onZoningLoaded={handleZoningLoaded}
        />

        {onViewChange && <ViewTracker onViewChange={onViewChange} />}
        <BaseLayerSwitchHandler />
        {/* #1131 — records base-layer / overlay toggles to sessionStorage. */}
        <LayerControlPersistence />

        {/* Split-view focus marker (#146 slice 2): when the user
            clicks a row in MapListPanel, focusedParcel is set, the
            FocusFlyer animates the map there, and this highlight
            marker draws a sticky outlined ring at the parcel's
            centroid so the user can re-find what they clicked. */}
        <FocusFlyer focusedParcel={focusedParcel} />
        {focusedParcel && focusedParcel.lat != null && focusedParcel.lon != null && (
          <CircleMarker
            center={[focusedParcel.lat, focusedParcel.lon]}
            pathOptions={{
              color: '#ff6f00', weight: 3, fillOpacity: 0,
              dashArray: '4 2',
            }}
            radius={14}
            interactive={false}
          />
        )}
        {/* Hover flash marker — fires from MapListPanel row hover. */}
        {hoveredParcelId && (() => {
          const hovered = points.find((p) => p.parcel_id === hoveredParcelId);
          if (!hovered || hovered.lat == null || hovered.lon == null) return null;
          return (
            <CircleMarker
              center={[hovered.lat, hovered.lon]}
              pathOptions={{
                color: '#1976d2', weight: 2, fillOpacity: 0.2,
                fillColor: '#1976d2',
              }}
              radius={10}
              interactive={false}
            />
          );
        })()}

        {/* Federal land polygons (rendered underneath parcel boundaries) */}
        {/* React-Leaflet's <GeoJSON> only reconciles `style` changes —
            not `data` — so a key change is required to remount when
            the data swaps. We derive the key from the data via a
            WeakMap-backed counter (geoKey) so every data setState
            naturally produces a fresh key, without needing a
            separate counter-state setter and its associated extra
            MapView re-render. */}
        {showFederalLands && federalLandsData && federalLandsData.features && federalLandsData.features.length > 0 && (
          <GeoJSON
            key={geoKey(federalLandsData)}
            data={federalLandsData}
            style={_federalLandsStyle}
            onEachFeature={_federalLandsOnEachFeature}
          />
        )}

        {/* Mining claim polygons */}
        {showMiningClaims && miningClaimsData && miningClaimsData.features && miningClaimsData.features.length > 0 && (
          <GeoJSON
            key={geoKey(miningClaimsData)}
            data={miningClaimsData}
            style={_miningClaimsStyle}
            onEachFeature={_miningClaimsOnEachFeature}
          />
        )}

        {/* Zoning district polygons */}
        {showZoning && zoningData && zoningData.features && zoningData.features.length > 0 && (
          <GeoJSON
            key={geoKey(zoningData)}
            data={zoningData}
            style={_zoningStyle}
            onEachFeature={_zoningOnEachFeature}
          />
        )}

        {/* Parcel boundary polygons (loaded at zoom >= 12 when the
            "Parcel Boundaries" toggle is enabled). Gated on
            `showBoundaries` so the overlay doesn't appear without an
            explicit user opt-in (issue #110). */}
        {showBoundaries && visibleBoundaryData && visibleBoundaryData.features && visibleBoundaryData.features.length > 0 && (
          <GeoJSON
            key={geoKey(visibleBoundaryData)}
            data={visibleBoundaryData}
            style={boundariesStyle}
            onEachFeature={boundariesOnEachFeature}
          />
        )}

        <MarkerClusterGroup
          chunkedLoading
          maxClusterRadius={60}
          spiderfyOnMaxZoom
          showCoverageOnHover={false}
          iconCreateFunction={createClusterIcon}
          disableClusteringAtZoom={16}
        >
          {clusterChildren}
        </MarkerClusterGroup>

        {/* Mine location markers (diamonds, color-coded by commodity) */}
        {showMines && (
          <LayerGroup>
            {minePoints.map((mine) => {
              const mColor = getMineColor(mine.commodity);
              return (
                <Marker
                  key={`mine-${mine.id}`}
                  position={[mine.lat, mine.lon]}
                  icon={getDiamondIcon(mColor.fill, mColor.border)}
                >
                  <Popup maxWidth={280} minWidth={200}>
                    <div style={{ fontSize: '0.85rem' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 4 }}>
                        <div style={{ fontWeight: 700, color: '#1a1a2e', flex: 1 }}>
                          {mine.site_name || 'Unnamed Site'}
                        </div>
                        {mine.dev_status && (() => {
                          const sc = STATUS_COLORS[mine.dev_status] || STATUS_COLORS.Unknown;
                          return (
                            <span style={{
                              background: sc.bg, color: sc.text, fontSize: '0.65rem', fontWeight: 700,
                              padding: '1px 6px', borderRadius: 8, whiteSpace: 'nowrap', marginLeft: 6,
                            }}>{mine.dev_status}</span>
                          );
                        })()}
                      </div>
                      {mine.commodity && (
                        <div style={{ marginBottom: 2 }}>
                          <svg width="10" height="10" viewBox="0 0 10 10" style={{ verticalAlign: 'middle', marginRight: 6 }}>
                            <rect x="1" y="1" width="8" height="8" rx="1" transform="rotate(45 5 5)"
                              fill={mColor.fill} stroke={mColor.border} strokeWidth="1" />
                          </svg>
                          <strong>Commodity:</strong> {mine.commodity}
                        </div>
                      )}
                      {mine.oper_type && mine.oper_type !== 'Unknown' && (
                        <div style={{ marginBottom: 2 }}>
                          <strong>Operation:</strong> {mine.oper_type}
                        </div>
                      )}
                      {mine.dep_type && (
                        <div style={{ marginBottom: 2 }}>
                          <strong>Deposit Type:</strong> {mine.dep_type}
                        </div>
                      )}
                    </div>
                  </Popup>
                </Marker>
              );
            })}
          </LayerGroup>
        )}
      </MapContainer>

      {/* Status overlay */}
      {loading && (
        <div className="map-status-overlay">
          <div className="spinner" style={{ width: 18, height: 18 }}></div>
          <span>Loading...</span>
        </div>
      )}
      {!loading && dataInfo.bbox_too_large && (
        <div className="map-status-overlay map-status-warning">
          Zoom in to load parcels (current view is too large to render).
        </div>
      )}
      {!loading && !dataInfo.bbox_too_large && dataInfo.count >= dataInfo.limit && (
        <div className="map-status-overlay map-status-warning">
          Showing {dataInfo.limit.toLocaleString()} of many parcels. Zoom in to see all.
        </div>
      )}

      {/* Overlay toggle buttons */}
      <div className="map-overlay-toggles">
        {/* #1043 — 3D tilt view of the main map (perspective on the whole
            map surface; also gives a raked view of the InSAR overlay). */}
        <button
          className={`overlay-toggle ${is3D ? 'active' : ''}`}
          onClick={() => setIs3D(v => !v)}
          title="Toggle 3D perspective (tilt) view of the map"
          aria-pressed={is3D}
          data-testid="map-3d-toggle"
        >
          <span className="map-3d-icon" aria-hidden="true">◢</span>
          3D
        </button>
        <button
          className={`overlay-toggle ${showBoundaries ? 'active' : ''}`}
          onClick={() => setShowBoundaries(v => !v)}
          title="Toggle parcel boundary polygons (loaded at zoom 8+)"
        >
          <span className="legend-dot" style={{ backgroundColor: '#3498db', display: 'inline-block' }}></span>
          Parcel Boundaries
        </button>
        <button
          className={`overlay-toggle ${showFederalLands ? 'active' : ''}`}
          onClick={() => setShowFederalLands(v => !v)}
          title="Toggle federal land boundaries (BLM, USFS, NPS, etc.)"
        >
          <span className="legend-dot" style={{ backgroundColor: '#F4A460', display: 'inline-block' }}></span>
          Federal Lands
        </button>
        <button
          className={`overlay-toggle ${showMiningClaims ? 'active' : ''}`}
          onClick={() => setShowMiningClaims(v => !v)}
          title="Toggle BLM active mining claims overlay"
        >
          <span className="legend-dot" style={{ backgroundColor: '#E74C3C', display: 'inline-block' }}></span>
          Mining Claims
        </button>
        <button
          className={`overlay-toggle ${showZoning ? 'active' : ''}`}
          onClick={() => setShowZoning(v => !v)}
          title="Toggle zoning district boundaries"
        >
          <span className="legend-dot" style={{ backgroundColor: '#8BC34A', display: 'inline-block' }}></span>
          Zoning
        </button>
        <button
          className={`overlay-toggle ${showHeatmap ? 'active' : ''}`}
          onClick={() => setShowHeatmap(v => !v)}
          title="Toggle heatmap overlay"
        >
          <span className="legend-dot" style={{ background: 'linear-gradient(90deg, #00c864, #ffcc00, #ff3300)', display: 'inline-block' }}></span>
          Heatmap
        </button>
        {showHeatmap && (
          <select
            value={heatmapLayer}
            onChange={(e) => setHeatmapLayer(e.target.value)}
            style={{
              padding: '4px 8px', borderRadius: 6, border: '1px solid #ccc',
              fontSize: '0.8rem', background: '#fff', cursor: 'pointer',
            }}
            title="Select heatmap layer"
          >
            {/* #936 — ML "predicted probability" layers held from users until
                the de-leaked model clears a per-state AUC bar (v3.8 is
                anti-predictive in NV/WA/OR gold). Re-expose via
                REACT_APP_COMMODITY_PREDICTIONS=true. */}
            {COMMODITY_PREDICTIONS_ENABLED && (
              <optgroup label="Mineral predictions (ML)">
                <option value="gold-prob">Gold — predicted probability</option>
                <option value="copper-prob">Copper — predicted probability</option>
                {/* #1381 item 5: uranium/lithium/REE withdrawn, two
                    calibrators are inverted and one is missing, so the
                    scorer no longer writes them and the freshness gate
                    filters what they last wrote. The control could not
                    succeed. Their "known mines" layers below are real
                    USGS MRDS data and are unaffected.
                    #1468: silver withdrawn for the opposite reason. Its
                    calibrator is healthy, but the grid carries none of the
                    geochem/fault/radiometric columns the model was trained
                    on, so it falls through to a constant 0.8936 and keeps
                    95.50% of the country. A layer that says yes everywhere
                    is not a ranking. */}
                <option value="niobium-prob">Niobium — predicted probability</option>
              </optgroup>
            )}
            <optgroup label="Known mines (USGS MRDS)">
              <option value="gold-mines">Gold — known mines</option>
              <option value="copper-mines">Copper — known mines</option>
              <option value="silver-mines">Silver — known mines</option>
              <option value="uranium-mines">Uranium — known mines</option>
              <option value="lithium-mines">Lithium — known mines</option>
              <option value="ree-mines">REE — known mines</option>
              <option value="niobium-mines">Niobium — known mines</option>
            </optgroup>
            {/* #936 — "combined" layers also render from the v3.8 grid, so
                they are held alongside the -prob layers. */}
            {COMMODITY_PREDICTIONS_ENABLED && (
              <optgroup label="Mineral combined (legacy)">
                <option value="gold">Gold (combined)</option>
                <option value="copper">Copper (combined)</option>
                {/* uranium/lithium/REE/silver withdrawn here too: the
                    combined layers render from the same grid rows
                    (#1381 item 5, #1468). */}
              </optgroup>
            )}
            <optgroup label="Wildfire">
              <option value="wildfire">Fire fuel/probability (USFS WHP)</option>
              <option value="wildfire-loss">Expected loss ($, FEMA NRI)</option>
              <option value="wildfire-freq">Wildfire Frequency (FEMA NRI AFREQ)</option>
            </optgroup>
            <optgroup label="Flood (FEMA NRI)">
              <option value="flood">Flood Loss Risk</option>
              <option value="flood-freq">Flood Frequency</option>
            </optgroup>
          </select>
        )}
        {/* Live render-parameter sliders: tweak the L.heatLayer
            radius/blur/max/minOpacity in real time. Defaults match the
            HeatmapLayer baseline for the active layer class so the
            initial render is unchanged from the pre-slider behavior.
            Hidden when a grid-backed commodity layer is active — those
            knobs (radius/blur/max/minOpacity) are leaflet.heat point
            accumulator params with no meaning for a pre-rendered raster. */}
        {showHeatmap && !gridCommodity && (
          <div
            className="heatmap-sliders"
            style={{
              display: 'flex', flexDirection: 'column', gap: 4,
              padding: '6px 8px', borderRadius: 6, border: '1px solid #ccc',
              background: '#fff', fontSize: '0.75rem', minWidth: 200,
            }}
          >
            <label style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 6 }}>
              <span>Radius: {heatRadius}</span>
              <input
                type="range"
                min={10}
                max={200}
                step={1}
                value={heatRadius}
                onChange={(e) => setHeatRadius(Number(e.target.value))}
                aria-label="Heatmap radius"
                style={{ flex: 1 }}
              />
            </label>
            <label style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 6 }}>
              <span>Blur: {heatBlur}</span>
              <input
                type="range"
                min={10}
                max={200}
                step={1}
                value={heatBlur}
                onChange={(e) => setHeatBlur(Number(e.target.value))}
                aria-label="Heatmap blur"
                style={{ flex: 1 }}
              />
            </label>
            <label style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 6 }}>
              <span>Max: {heatMax.toFixed(1)}</span>
              <input
                type="range"
                min={0.1}
                max={5.0}
                step={0.1}
                value={heatMax}
                onChange={(e) => setHeatMax(Number(e.target.value))}
                aria-label="Heatmap intensity ceiling"
                style={{ flex: 1 }}
              />
            </label>
            <label style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 6 }}>
              <span>Min opacity: {heatMinOpacity.toFixed(2)}</span>
              <input
                type="range"
                min={0.0}
                max={0.5}
                step={0.01}
                value={heatMinOpacity}
                onChange={(e) => setHeatMinOpacity(Number(e.target.value))}
                aria-label="Heatmap minimum opacity"
                style={{ flex: 1 }}
              />
            </label>
          </div>
        )}
        {/* Grid raster path: the only adjustable knob is layer opacity —
            color/intensity are baked into the precomputed PNG tiles. Gated
            with the prediction hold (#936) so no opacity control shows for a
            held commodity layer. */}
        {COMMODITY_PREDICTIONS_ENABLED && showHeatmap && gridCommodity && (
          <div
            className="heatmap-sliders"
            style={{
              display: 'flex', flexDirection: 'column', gap: 4,
              padding: '6px 8px', borderRadius: 6, border: '1px solid #ccc',
              background: '#fff', fontSize: '0.75rem', minWidth: 200,
            }}
          >
            <label style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 6 }}>
              <span>Opacity: {heatTileOpacity.toFixed(2)}</span>
              <input
                type="range"
                min={0.1}
                max={1.0}
                step={0.05}
                value={heatTileOpacity}
                onChange={(e) => setHeatTileOpacity(Number(e.target.value))}
                aria-label="Heatmap layer opacity"
                style={{ flex: 1 }}
              />
            </label>
          </div>
        )}
        {/* Tract overlay — only meaningful when a hazard heatmap is
            active; hidden otherwise so it doesn't clutter the panel for
            commodity-layer users. Shown alongside the heatmap so users
            can compare the smoothed surface against the raw NRI tract
            centroids. */}
        {showHeatmap && isHazardLayer && (
          <label
            className={`overlay-toggle ${showHazardDebug ? 'active' : ''}`}
            style={{ cursor: 'pointer', userSelect: 'none' }}
            title="Overlay raw NRI census-tract centroids (with scores) on top of the heatmap so you can compare the smoothed surface against the underlying source data."
          >
            <input
              type="checkbox"
              checked={showHazardDebug}
              onChange={(e) => setShowHazardDebug(e.target.checked)}
              style={{ marginRight: 4 }}
            />
            Show tracts (compare)
            <span
              style={{ marginLeft: 6, fontSize: '0.7rem', color: '#666', fontWeight: 'normal' }}
              title="Overlay raw NRI census-tract centroids alongside the heatmap for comparison"
            >
              — overlay raw tract scores for comparison
            </span>
          </label>
        )}
        <button
          className={`overlay-toggle ${showMines ? 'active' : ''}`}
          onClick={() => setShowMines(v => !v)}
          title="Toggle mineral deposit locations"
        >
          <span className="legend-dot" style={{ backgroundColor: '#ff9900', display: 'inline-block' }}></span>
          Mineral Deposits
        </button>
        {showMines && (
          <>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              style={{
                padding: '4px 8px', borderRadius: 6, border: '1px solid #ccc',
                fontSize: '0.8rem', background: '#fff', cursor: 'pointer',
              }}
              title="Filter by site type"
            >
              {STATUS_FILTER_OPTIONS.map(s => (
                <option key={s.value} value={s.value}>{s.label}</option>
              ))}
            </select>
            <select
              value={commodityFilter}
              onChange={(e) => setCommodityFilter(e.target.value)}
              style={{
                padding: '4px 8px', borderRadius: 6, border: '1px solid #ccc',
                fontSize: '0.8rem', background: '#fff', cursor: 'pointer',
              }}
              title="Filter by mineral type"
            >
              {COMMODITY_FILTER_OPTIONS.map(c => (
                <option key={c} value={c}>{c}</option>
              ))}
            </select>
          </>
        )}
      </div>

      {/* Legend */}
      <div className="map-legend">
        <h4>Parcels</h4>
        <div className="legend-item">
          <span className="legend-dot" style={{ backgroundColor: '#2ecc71' }}></span>
          <span>Listed (with boundary)</span>
        </div>
        <div className="legend-item">
          <span className="legend-dot" style={{ backgroundColor: '#95d5b2', border: '1px dashed #82c99a' }}></span>
          <span>Listed (no boundary)</span>
        </div>
        <div className="legend-item">
          <span className="legend-dot" style={{ backgroundColor: '#3498db' }}></span>
          <span>Not listed</span>
        </div>
        {showMines && (
          <>
            <h4 style={{ marginTop: 8 }}>Deposits by Mineral</h4>
            {Object.entries(COMMODITY_COLORS).slice(0, 8).map(([name, colors]) => (
              <div key={name} className="legend-item">
                <svg width="12" height="12" viewBox="0 0 12 12" style={{ flexShrink: 0 }}>
                  <rect x="1.5" y="1.5" width="9" height="9" rx="1" transform="rotate(45 6 6)"
                    fill={colors.fill} stroke={colors.border} strokeWidth="1" />
                </svg>
                <span>{name}</span>
              </div>
            ))}
            <div className="legend-item">
              <svg width="12" height="12" viewBox="0 0 12 12" style={{ flexShrink: 0 }}>
                <rect x="1.5" y="1.5" width="9" height="9" rx="1" transform="rotate(45 6 6)"
                  fill={DEFAULT_MINE_COLOR.fill} stroke={DEFAULT_MINE_COLOR.border} strokeWidth="1" />
              </svg>
              <span>Other</span>
            </div>
          </>
        )}
        {showFederalLands && (
          <>
            <h4 style={{ marginTop: 8 }}>Federal Lands</h4>
            {[
              ['BLM', 'BLM'],
              ['USFS', 'Forest Service'],
              ['NPS', 'Nat\'l Park Service'],
              ['FWS', 'Fish & Wildlife'],
              ['BIA', 'Indian Affairs'],
              ['BOR', 'Reclamation'],
              ['DOD', 'Dept. of Defense'],
              ['DOE', 'Dept. of Energy'],
            ].map(([code, label]) => (
              <div key={code} className="legend-item">
                <span className="legend-dot" style={{
                  backgroundColor: AGENCY_COLORS[code] || '#888',
                  opacity: 0.6,
                }}></span>
                <span>{label}</span>
              </div>
            ))}
          </>
        )}
        {showMiningClaims && (
          <>
            <h4 style={{ marginTop: 8 }}>Mining Claims</h4>
            {[
              ['LODE CLAIM', 'Lode Claim'],
              ['PLACER CLAIM', 'Placer Claim'],
              ['MILL SITE', 'Mill Site'],
              ['TUNNEL SITE', 'Tunnel Site'],
            ].map(([type, label]) => (
              <div key={type} className="legend-item">
                <span className="legend-dot" style={{
                  backgroundColor: CLAIM_TYPE_COLORS[type] || '#E74C3C',
                  opacity: 0.7,
                }}></span>
                <span>{label}</span>
              </div>
            ))}
          </>
        )}
      </div>
    </div>
  );
}

export default MapView;
